import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useClinicians } from '@features/organizations';
import {
  ArtifactDropOverlay,
  formatUploadLimitError,
  useAdminArtifactUploadConstraints,
  useAdminOrganizationArtifactEventStream,
  useArtifactUploadConstraints,
  useChartWideDropZone,
  useOptimisticUploads,
  useOrganizationArtifactEventStream,
  useScopedArtifactDownload,
  useUploadAdminOrganizationArtifacts,
  useUploadOrganizationArtifacts,
  validateFiles,
} from '@features/patient-artifacts';
import { useCan, useGroupUserRoles, useUserRoles } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import { Alert, Box, Button, LinearProgress } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { normalizeProviderLicenseTypes } from '@utils/providerLicenseTypes';
import { ROLES } from '@utils/roleUtils';
import { useSnackbar } from 'notistack';

import { triggerArchiveBlobDownload } from '../../documentArchiveDownload';
import {
  DOCUMENT_PROCESSING_POLL_INTERVAL_MS,
  documentInventoryPollingInterval,
  isDocumentProcessingComplete,
} from '../../documentPolling';
import {
  acknowledgePersistedFieldValue,
  durableAnswerableCompletion,
  durableRequiredCompletion,
  findIntakeItemForField,
  hydrateFieldStates,
  type IntakeFieldStates,
  intakeFieldValuesEqual,
  isPersistableIntakeField,
  maskedSensitiveValuesByFieldId,
  replaceFieldValueWithAcceptedSuggestion,
  updateFieldStatesForDraftChange,
} from '../../fieldPersistence';
import { useSerializedIntakeWrites } from '../../hooks/useSerializedIntakeWrites';
import {
  intakeItemAuthoritiesEqual,
  type IntakeItemAuthority,
  type IntakeItemAuthorityEntry,
  reconcileIntakeItems,
  storeMutationItem,
} from '../../itemAuthority';
import {
  hydrateRepeatRecords,
  type IntakeRepeatGroupStates,
  type IntakeRepeatRecord,
  reconcileRepeatGroupStates,
  repeatGroupKey,
  repeatRecordsEqual,
  repeatRecordsEvidence,
  suggestionEntryKey,
} from '../../repeatableFields';
import { intakePagePath, resolveIntakePageKey } from '../../routing';
import {
  useAcceptIntakeHubDocumentFieldSuggestionsMutation,
  useAddAdminIntakeProviderRosterEntryMutation,
  useAddIntakeProviderRosterEntryMutation,
  useDiscardAdminIntakeHubVerificationMutation,
  useDownloadAdminIntakeHubDocumentsArchiveMutation,
  useGenerateAdminIntakeFormMutation,
  useGenerateIntakeFormMutation,
  useGetAdminIntakeFormConfigurationQuery,
  useGetAdminIntakeHubAggregateQuery,
  useGetAdminIntakeHubDashboardQuery,
  useGetAdminIntakeHubDocumentsQuery,
  useGetIntakeFormConfigurationQuery,
  useGetIntakeHubDashboardQuery,
  useGetIntakeHubDocumentsQuery,
  useMaterializeAdminIntakeHubItemsMutation,
  useMaterializeIntakeHubItemsMutation,
  useRemoveAdminIntakeProviderRosterEntryMutation,
  useRemoveIntakeProviderRosterEntryMutation,
  useUpdateAdminIntakeFormConfigurationMutation,
  useUpdateAdminIntakeHubItemEvidenceMutation,
  useUpdateIntakeFormConfigurationMutation,
  useUpdateIntakeHubItemEvidenceMutation,
  useUpdateIntakeHubSensitiveFieldMutation,
  useVerifyAdminIntakeHubMutation,
} from '../../store/api';
import { SuggestionAcceptanceError } from '../../suggestionAcceptance';
import type {
  GeneratedIntakeField,
  GeneratedIntakeItem,
  IntakeFieldSuggestion,
  IntakeFieldSuggestionAcceptanceRequest,
  IntakeFieldSuggestionAcceptanceResponse,
  IntakeFieldValue,
  IntakeFormConfiguration,
  IntakeFormConfigurationUpdate,
  IntakeHubDocumentInventoryItem,
  IntakePayerRosterEntry,
} from '../../types';
import {
  canDisplayDocumentSuggestion,
  decomposedDocumentReferenceIds,
  intakeDocumentInventoryItemToDetail,
} from '../intakeDocumentDetail';

import { type AdminSaveKind, formatAdminSaveErrorMessage } from './adminSaveErrors';
import { AggregateIntakePage } from './AggregateIntakePage';
import { IntakeAdminPayerSearch } from './IntakeAdminPayerSearch';
import { IntakeCompletionPage } from './IntakeCompletionPage';
import { type EligibleProvider, type IntakeConfig, IntakeConfigPage } from './IntakeConfigPage';
import {
  type DocumentRequirementStatus,
  type FoundDocument,
  IntakeDocumentsPage,
} from './IntakeDocumentsPage';
import { IntakeFieldsPage } from './IntakeFieldsPage';
import { type ForecastDocument, IntakeForecastPage } from './IntakeForecastPage';
import { IntakeProviderRosterPage } from './IntakeProviderRosterPage';
import { IntakeReviewPage } from './IntakeReviewSubmitPage';
import { IntakeShell, type RailProgressState } from './IntakeShell';
import { RAIL_SECTIONS, TRAILING_PAGE_KEYS, TRAILING_SECTION } from './navigation';
import type { PayerRow } from './types';

const ORDERED_KEYS = RAIL_SECTIONS.flatMap(section => section.items.map(item => item.pageKey));

export type IntakeExperienceContext =
  | { kind: 'provider' }
  | {
      kind: 'admin';
      organizationId: string;
      userId?: string;
      providerEmail?: string;
      targetLabel?: string;
      eligibleProviders?: EligibleProvider[];
    };

interface IntakeExperienceProps {
  context?: IntakeExperienceContext;
  onDirtyChange?: (isDirty: boolean) => void;
}

const PROVIDER_CONTEXT: IntakeExperienceContext = { kind: 'provider' };
/** Practice address keeps primary-record-only semantics, server-side and here. */
const PRACTICE_ADDRESS_REQUIREMENT_KEY = 'organization.practice_address';

/** Documents pages get the drop zone; every other page renders fields. */
const DOCUMENT_PAGES: Record<string, { title: string; sub: string; hint: string }> = {
  M1: {
    title: 'Clinic documents',
    sub: 'Upload your W-9 here so it is included with your intake.',
    hint: "PDF or photo · You'll enter and confirm its details on the next pages",
  },
  P1: {
    title: 'Your documents',
    sub: 'Upload your supporting documents here so they are included with your intake.',
    hint: "PDF or photo · You'll enter and confirm their details on the next pages",
  },
};

function documentDisplayStatus(
  processingStatus?: string | null,
): 'parsing' | 'uploaded' | 'failed' {
  if (processingStatus === 'failed') return 'failed';
  if (isDocumentProcessingComplete(processingStatus)) return 'uploaded';
  return 'parsing';
}

function persistedDocumentToFoundDocument(
  documentItem: IntakeHubDocumentInventoryItem,
  fields: GeneratedIntakeField[],
  decomposedReferenceIds: ReadonlySet<string> = new Set(),
): FoundDocument {
  const documentReference = documentItem.document_reference;
  const isDecomposedChild = decomposedReferenceIds.has(documentReference.document_reference_id);
  const detail = intakeDocumentInventoryItemToDetail(documentItem, fields, { isDecomposedChild });
  return {
    artifactId: documentReference.source_artifact_id ?? null,
    filename: documentItem.original_filename ?? documentReference.title ?? 'Untitled document',
    status: documentDisplayStatus(documentReference.processing_status),
    docKind: documentReference.doc_kind ?? null,
    filledCount:
      (documentItem.field_suggestions?.length ?? 0) + (documentItem.extracted_fields?.length ?? 0),
    understood: documentReference.processing_status !== 'failed',
    detail,
  };
}

// TODO(compat): remove this guard once the I23 retirement (server #4337) is
// deployed to production and no environment can serve a generated form that
// still carries the I23 group.
// Retired-catalog guard: the Availity checkbox UI (group I23, e.g. field
// I23.1) was deleted from this surface, but a stale backend can still serve
// I23 as a required generated-form field. With no control left to satisfy
// it, counting it toward completion would make intake permanently
// unfinishable. Every place generated-form fields feed completion, percent,
// or "what to upload"/"what we need" tiles must filter these out first.
const RETIRED_GROUP_IDS = new Set(['I23']);

function isRetiredGroupField(field: GeneratedIntakeField): boolean {
  return RETIRED_GROUP_IDS.has(field.group_id);
}

function activeFormFields(fields: GeneratedIntakeField[]): GeneratedIntakeField[] {
  return fields.filter(field => !isRetiredGroupField(field));
}

function documentFields(fields: GeneratedIntakeField[]): GeneratedIntakeField[] {
  return activeFormFields(fields).filter(
    field =>
      field.is_writable !== false &&
      field.data_type.toLowerCase() === 'file' &&
      field.destination.kind === 'document_reference',
  );
}

function requiredDocumentFields(fields: GeneratedIntakeField[]): GeneratedIntakeField[] {
  return documentFields(fields).filter(field => field.is_required);
}

function normalizeDocumentKind(value?: string | null): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function fieldDocumentKinds(field: GeneratedIntakeField): string[] {
  const requirementKind = normalizeDocumentKind(field.requirement_key?.split('.').at(-1));
  const destinationKind =
    field.destination.kind === 'document_reference'
      ? normalizeDocumentKind(field.destination.field_key.replace(/_file$/, ''))
      : '';
  return [requirementKind, destinationKind].filter(Boolean);
}

function documentMatchesFields(
  document: FoundDocument,
  fields: GeneratedIntakeField[],
  items: GeneratedIntakeItem[],
  currentUserId?: string | null,
): boolean {
  const fieldsForCanvas = documentFields(fields);
  const linkedToCanvas = fieldsForCanvas.some(field => {
    const intakeItem = findIntakeItemForField(field, items, currentUserId);
    return (document.detail?.linkedItems ?? []).some(link =>
      intakeItem?.intake_item_id
        ? link.intake_item_id === intakeItem.intake_item_id
        : link.requirement_key === field.requirement_key,
    );
  });
  if (linkedToCanvas) return true;

  const documentKind = normalizeDocumentKind(document.docKind);
  return (
    documentKind.length > 0 &&
    fieldsForCanvas.some(field => fieldDocumentKinds(field).includes(documentKind))
  );
}

interface FieldSatisfaction {
  satisfied: boolean;
  satisfiedByFilename?: string;
  /** How many further documents satisfy this beyond the one named. */
  satisfiedByAdditionalCount?: number;
  satisfiedDocument?: FoundDocument;
  /** Every document satisfying this, newest first. */
  satisfiedDocuments?: FoundDocument[];
}

/**
 * Every document satisfying an item, newest first.
 *
 * `satisfied_by_document_reference_ids` is the full list; the singular field
 * stays populated with the most recent, so it is the fallback for a payload
 * from a server that predates the list.
 */
function satisfyingDocumentReferenceIds(item?: GeneratedIntakeItem): string[] {
  // TODO(compat): drop the singular-field fallback below once every
  // production backend serves satisfied_by_document_reference_ids
  // (server #4317 promoted to prod).
  const all = item?.satisfied_by_document_reference_ids;
  if (all && all.length > 0) return all;
  const singular = item?.satisfied_by_document_reference_id;
  return singular ? [singular] : [];
}

/**
 * Per-document-field satisfaction, read straight off the backing dashboard
 * item's `is_complete` / `satisfied_by_document_reference_id` rather than
 * re-deriving it with the fuzzy `documentMatchesFields` doc-kind match.
 *
 * When the satisfying document resolves to a `FoundDocument` (via
 * `documentByReferenceId`), it's threaded through so a satisfied tile/row can
 * open the exact same preview dialog the "Documents received" list uses,
 * instead of duplicating dialog state.
 */
function fieldSatisfaction(
  field: GeneratedIntakeField,
  items: GeneratedIntakeItem[],
  currentUserId: string | null | undefined,
  documentByReferenceId: Record<string, FoundDocument>,
): FieldSatisfaction {
  const item = findIntakeItemForField(field, items, currentUserId);
  const resolved = satisfyingDocumentReferenceIds(item).flatMap(referenceId => {
    const document = documentByReferenceId[referenceId];
    return document ? [document] : [];
  });
  return satisfactionFromDocuments(Boolean(item?.is_complete), resolved);
}

/** Collapses the satisfying documents into what a tile or row renders. */
function satisfactionFromDocuments(
  satisfied: boolean,
  documents: FoundDocument[],
): FieldSatisfaction {
  const satisfiedDocument = documents[0];
  return {
    satisfied,
    satisfiedByFilename: satisfiedDocument?.filename,
    satisfiedByAdditionalCount: documents.length > 1 ? documents.length - 1 : undefined,
    satisfiedDocument,
    satisfiedDocuments: documents,
  };
}

/**
 * Satisfaction for a group of document fields that share one "what we need"
 * forecast tile — satisfied only once every field backing the tile has a
 * completed dashboard item, mirroring how a single required document's
 * status is read via `fieldSatisfaction`.
 */
function groupSatisfaction(
  fields: GeneratedIntakeField[],
  items: GeneratedIntakeItem[],
  currentUserId: string | null | undefined,
  documentByReferenceId: Record<string, FoundDocument>,
): FieldSatisfaction {
  if (fields.length === 0) return { satisfied: false };
  // A tile mixing required and optional document fields is satisfied by its
  // required fields alone — the section this replaced only ever considered
  // required fields, and an optional sibling must not hold a tile hostage.
  const requiredFields = fields.filter(field => field.is_required);
  const basis = requiredFields.length > 0 ? requiredFields : fields;
  const results = basis.map(field =>
    fieldSatisfaction(field, items, currentUserId, documentByReferenceId),
  );
  const satisfied = results.every(result => result.satisfied);
  if (!satisfied) return { satisfied: false };
  // One tile can be backed by several fields, and a provider's two licences
  // satisfy the same tile through different documents. De-duplicate so a
  // document counted twice does not inflate the "+N".
  const seen = new Set<string>();
  const documents = results
    .flatMap(result => result.satisfiedDocuments ?? [])
    .filter(document => {
      const key = document.detail?.id ?? document.artifactId ?? document.filename;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return satisfactionFromDocuments(true, documents);
}

/**
 * Per-required-document satisfaction for the "What to upload" list. Groups
 * required document fields onto their backing dashboard item (same join the
 * upload-canvas rail completion uses) and reads satisfaction straight off
 * `is_complete` / `satisfied_by_document_reference_id` rather than
 * re-deriving it with the fuzzy `documentMatchesFields` doc-kind match.
 *
 * When the satisfying document resolves to a `FoundDocument` (via
 * `documentByReferenceId`), it's threaded through so the requirement row can
 * open the exact same preview dialog the "Documents received" list uses,
 * instead of duplicating dialog state.
 */
function documentRequirementStatuses(
  fields: GeneratedIntakeField[],
  items: GeneratedIntakeItem[],
  currentUserId: string | null | undefined,
  documentByReferenceId: Record<string, FoundDocument>,
): DocumentRequirementStatus[] {
  const grouped = new Map<
    string,
    { label: string; item?: GeneratedIntakeItem; groupId?: string }
  >();
  requiredDocumentFields(fields).forEach(field => {
    const item = findIntakeItemForField(field, items, currentUserId);
    const key = item?.intake_item_id ?? `${field.view}:${field.requirement_key ?? field.field_id}`;
    if (!grouped.has(key)) {
      grouped.set(key, { label: field.label, item, groupId: field.group_id });
    }
  });
  return [...grouped.entries()].map(([key, { label, item, groupId }]) => {
    const referenceId = item?.satisfied_by_document_reference_id ?? null;
    const satisfiedDocument = referenceId ? documentByReferenceId[referenceId] : undefined;
    return {
      key,
      label,
      groupId,
      satisfied: Boolean(item?.is_complete),
      satisfiedByFilename: satisfiedDocument?.filename,
      satisfiedDocument,
    };
  });
}

function pageLabel(pageKey: string): string {
  return (
    RAIL_SECTIONS.flatMap(section => section.items).find(item => item.pageKey === pageKey)?.label ??
    pageKey
  );
}

function defaultIntakeConfig(): IntakeConfig {
  return {
    role: 'manager_who_also_practices',
    products: [],
    isPrescriber: true,
    licenseTypes: ['PMHNP'],
    practiceType: 'group',
    completedPages: [],
  };
}

function serverConfigurationToIntakeConfig(configuration: IntakeFormConfiguration): IntakeConfig {
  return {
    role: configuration.role,
    products: configuration.products ?? [],
    isPrescriber: configuration.is_prescriber ?? false,
    licenseTypes: normalizeProviderLicenseTypes(configuration.license_types ?? []) ?? [],
    practiceType: configuration.practice_type ?? 'group',
    completedPages: configuration.completed_pages ?? [],
  };
}

function intakeConfigurationUpdate(config: IntakeConfig): IntakeFormConfigurationUpdate {
  return {
    role: config.role,
    practice_type: config.practiceType,
    is_prescriber: config.isPrescriber,
    license_types: config.licenseTypes,
    completed_pages: config.completedPages,
  };
}

function configurationsMatch(left: IntakeConfig, right: IntakeConfig): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function suggestionIdentity(suggestion: IntakeFieldSuggestion): string {
  return [
    suggestion.target_intake_item_id,
    suggestion.extraction_id,
    suggestion.field_id,
    suggestion.repeat_record_id ?? '',
  ].join(':');
}

function acceptanceRequestIdentity(request: IntakeFieldSuggestionAcceptanceRequest): string {
  return JSON.stringify([
    request.intake_item_id,
    request.document_reference_id,
    request.extraction_id,
    request.field_id,
    request.repeat_record_id ?? '',
  ]);
}

function acceptanceOperationIdentity(requests: IntakeFieldSuggestionAcceptanceRequest[]): string {
  return JSON.stringify(requests.map(acceptanceRequestIdentity).sort());
}

function hasExactlyCorrelatedAcceptanceResults(
  requests: IntakeFieldSuggestionAcceptanceRequest[],
  response: unknown,
): response is IntakeFieldSuggestionAcceptanceResponse {
  if (!response || typeof response !== 'object') return false;
  const results = (response as { results?: unknown }).results;
  if (!Array.isArray(results) || results.length !== requests.length) return false;

  return results.every((result: unknown, index) => {
    if (!result || typeof result !== 'object') return false;
    const record = result as Record<string, unknown>;
    const request = requests[index];
    return (
      record.intake_item_id === request.intake_item_id &&
      record.document_reference_id === request.document_reference_id &&
      record.extraction_id === request.extraction_id &&
      record.field_id === request.field_id &&
      (record.repeat_record_id ?? null) === (request.repeat_record_id ?? null) &&
      (record.outcome === 'accepted' ||
        record.outcome === 'conflict' ||
        record.outcome === 'not_found')
    );
  });
}

export function IntakeExperience({
  context = PROVIDER_CONTEXT,
  onDirtyChange,
}: IntakeExperienceProps) {
  const isAdmin = context.kind === 'admin';
  const userRoles = useUserRoles();
  const groupUserRoles = useGroupUserRoles();
  const canVerifyIntake = isAdmin && userRoles.includes('Admin');
  // Intake scope follows authenticated membership, never the editable form role.
  const providerView =
    userRoles.includes(ROLES.PRACTICE_ADMINISTRATOR) ||
    groupUserRoles.includes(ROLES.PRACTICE_ADMINISTRATOR)
      ? 'all'
      : 'individual';
  // Only the staff surface without a selected user is truly aggregate/read-only.
  // Practice managers request the all-scope dashboard for visibility, but the
  // backend preserves their authenticated user_id so their own clinic intake
  // remains writable.
  const isAggregateView = context.kind === 'admin' && !context.userId;
  const location = useLocation();
  const navigate = useNavigate();
  const { intakeHub } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const getDocumentUrl = useScopedArtifactDownload(
    context.kind === 'admin'
      ? { kind: 'admin', organizationId: context.organizationId }
      : { kind: 'provider' },
  );
  const [downloadAdminDocumentsArchive, { isLoading: isDownloadingAllDocuments }] =
    useDownloadAdminIntakeHubDocumentsArchiveMutation();
  // Depend on the context's primitives, not the object: callers pass
  // `context` as an inline literal whose identity changes every render.
  const contextKind = context.kind;
  const contextOrganizationId = context.kind === 'admin' ? context.organizationId : undefined;
  const contextUserId = context.kind === 'admin' ? context.userId : undefined;
  const handleDownloadAllDocuments = useCallback(async () => {
    if (contextKind !== 'admin' || !contextOrganizationId) return;
    try {
      const download = await downloadAdminDocumentsArchive({
        organizationId: contextOrganizationId,
        userId: contextUserId,
      }).unwrap();
      triggerArchiveBlobDownload(download);
    } catch (error) {
      console.error('Failed to download intake documents', error);
      enqueueSnackbar('Could not download the document archive yet.', { variant: 'error' });
    }
  }, [
    contextKind,
    contextOrganizationId,
    contextUserId,
    downloadAdminDocumentsArchive,
    enqueueSnackbar,
  ]);
  const [config, setConfig] = useState<IntakeConfig>(defaultIntakeConfig);
  const [acknowledgedConfig, setAcknowledgedConfig] = useState<IntakeConfig>(defaultIntakeConfig);
  const [hydratedConfigurationScope, setHydratedConfigurationScope] = useState<string | null>(null);
  const adminStep = new URLSearchParams(location.search).get('intakeStep');
  const routedPageKey = isAdmin
    ? resolveIntakePageKey(`/intake/${adminStep ?? ''}`)
    : resolveIntakePageKey(location.pathname);
  const currentKey = routedPageKey ?? 'K1';
  const [gateOpenedInSession, setGateOpenedInSession] = useState(false);
  const [fieldStates, setFieldStates] = useState<IntakeFieldStates>({});
  const [pendingSensitiveConfirmations, setPendingSensitiveConfirmations] = useState<Set<string>>(
    () => new Set(),
  );
  const [discardedSuggestionIds, setDiscardedSuggestionIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isSavingAdminChanges, setIsSavingAdminChanges] = useState(false);
  const [isSavingPageCompletion, setIsSavingPageCompletion] = useState(false);
  const [adminSaveErrorCount, setAdminSaveErrorCount] = useState(0);
  const [adminSaveErrorMessage, setAdminSaveErrorMessage] = useState<string>();
  const [completionErrorPageKey, setCompletionErrorPageKey] = useState<
    IntakeConfig['completedPages'][number] | null
  >(null);
  const [docKinds, setDocKinds] = useState<
    Record<string, { docKind: string | null; filled: number }>
  >({});
  const [aggregatePage, setAggregatePage] = useState(1);

  const configurationScope =
    context.kind === 'admin'
      ? `${context.organizationId}:${context.userId ?? 'all-users'}`
      : 'provider';
  const itemAuthorityScope =
    context.kind === 'admin' ? configurationScope : `provider:${providerView}`;
  const [itemAuthorityState, setItemAuthorityState] = useState<{
    authority: IntakeItemAuthority;
    scope: string;
  }>(() => ({ authority: new Map(), scope: itemAuthorityScope }));
  const scopedItemAuthority = useMemo<IntakeItemAuthority>(
    () =>
      itemAuthorityState.scope === itemAuthorityScope
        ? itemAuthorityState.authority
        : new Map<string, IntakeItemAuthorityEntry>(),
    [itemAuthorityScope, itemAuthorityState],
  );
  const {
    currentData: providerConfiguration,
    error: providerConfigurationError,
    isFetching: isProviderConfigurationFetching,
    refetch: refetchProviderConfiguration,
  } = useGetIntakeFormConfigurationQuery(undefined, { skip: isAdmin });
  const {
    currentData: adminConfiguration,
    error: adminConfigurationError,
    isFetching: isAdminConfigurationFetching,
    refetch: refetchAdminConfiguration,
  } = useGetAdminIntakeFormConfigurationQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? (context.userId ?? '') : '',
    },
    { skip: !isAdmin || context.kind !== 'admin' || !context.userId },
  );
  const serverConfiguration = isAdmin ? adminConfiguration : providerConfiguration;
  const gateOpen =
    isAggregateView || gateOpenedInSession || Boolean(serverConfiguration?.last_change);
  const configurationError = isAdmin ? adminConfigurationError : providerConfigurationError;
  const isConfigurationFetching = isAdmin
    ? isAdminConfigurationFetching
    : isProviderConfigurationFetching;
  const refetchConfiguration = isAdmin ? refetchAdminConfiguration : refetchProviderConfiguration;
  const {
    currentData: providerDashboard,
    error: providerDashboardError,
    isFetching: isProviderDashboardFetching,
    refetch: refetchProviderDashboard,
  } = useGetIntakeHubDashboardQuery({ experience: 'v2', view: providerView }, { skip: isAdmin });
  const {
    currentData: adminDashboard,
    error: adminDashboardError,
    isFetching: isAdminDashboardFetching,
    refetch: refetchAdminDashboard,
  } = useGetAdminIntakeHubDashboardQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? context.userId : undefined,
      experience: 'v2',
    },
    { skip: !isAdmin },
  );
  const rawDashboard = isAdmin ? adminDashboard : providerDashboard;
  const reconciledDashboardItems = useMemo(
    () => reconcileIntakeItems(rawDashboard?.items ?? [], scopedItemAuthority),
    [rawDashboard?.items, scopedItemAuthority],
  );
  useEffect(() => {
    setItemAuthorityState(previous => {
      const previousAuthority =
        previous.scope === itemAuthorityScope
          ? previous.authority
          : new Map<string, IntakeItemAuthorityEntry>();
      if (intakeItemAuthoritiesEqual(previousAuthority, reconciledDashboardItems.authority)) {
        return previous.scope === itemAuthorityScope
          ? previous
          : { authority: previousAuthority, scope: itemAuthorityScope };
      }
      return { authority: reconciledDashboardItems.authority, scope: itemAuthorityScope };
    });
  }, [itemAuthorityScope, reconciledDashboardItems.authority]);
  const dashboard = useMemo(
    () =>
      rawDashboard ? { ...rawDashboard, items: reconciledDashboardItems.items } : rawDashboard,
    [rawDashboard, reconciledDashboardItems.items],
  );
  // journey_status.submitted_at is server truth; the client-side completion and
  // gate computations can disagree with it (a newly required field, an
  // unacknowledged Availity field in admin hydration, a hydration race). The
  // admin verify and discard controls live only on X1, so an admin looking at a
  // submitted intake must be able to reach X1 even then — otherwise
  // verification has no reachable surface at all.
  const adminVerificationNeedsCompletionPage =
    context.kind === 'admin' && Boolean(dashboard?.journey_status?.submitted_at);
  const dashboardError = isAdmin ? adminDashboardError : providerDashboardError;
  const isDashboardFetching = isAdmin ? isAdminDashboardFetching : isProviderDashboardFetching;
  const refetchDashboard = isAdmin ? refetchAdminDashboard : refetchProviderDashboard;
  const {
    currentData: aggregateView,
    error: aggregateViewError,
    isFetching: isAggregateViewFetching,
    refetch: refetchAggregateView,
  } = useGetAdminIntakeHubAggregateQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      page: aggregatePage,
      perPage: 20,
    },
    { skip: !isAggregateView },
  );
  const payerRosterItem = useMemo(
    () =>
      (dashboard?.items ?? []).find(
        item => item.requirement_key === 'jotbill.payer_roster' && item.user_id == null,
      ),
    [dashboard?.items],
  );
  const persistedPayerRoster = useMemo(
    () => payerRosterItem?.evidence_summary?.payer_roster ?? [],
    [payerRosterItem?.evidence_summary?.payer_roster],
  );
  const normalizedPersistedPayerRoster = useMemo(
    () =>
      persistedPayerRoster.map(payer => ({
        ...payer,
        enrollment_status: payer.enrollment_status ?? 'already_enrolled',
        jotbill: payer.jotbill ?? false,
        // jotcred is derived from the section: always on for payers who want
        // to get credentialed, never on for payers credentialed elsewhere.
        jotcred: (payer.enrollment_status ?? 'already_enrolled') === 'wants_to_enroll',
      })),
    [persistedPayerRoster],
  );
  const [payerRosterDraft, setPayerRosterDraft] = useState<IntakePayerRosterEntry[]>([]);
  const [acknowledgedPayerRoster, setAcknowledgedPayerRoster] = useState<IntakePayerRosterEntry[]>(
    [],
  );
  const payerRosterDirty =
    JSON.stringify(payerRosterDraft) !== JSON.stringify(acknowledgedPayerRoster);
  const hydratedPayerRosterItemId = useRef<string | null>(null);

  useEffect(() => {
    const itemId = payerRosterItem?.intake_item_id ?? null;
    if (hydratedPayerRosterItemId.current !== itemId) {
      hydratedPayerRosterItemId.current = itemId;
      setPayerRosterDraft(normalizedPersistedPayerRoster);
      setAcknowledgedPayerRoster(normalizedPersistedPayerRoster);
      return;
    }
    if (!payerRosterDirty) {
      setPayerRosterDraft(normalizedPersistedPayerRoster);
      setAcknowledgedPayerRoster(normalizedPersistedPayerRoster);
    }
  }, [normalizedPersistedPayerRoster, payerRosterDirty, payerRosterItem?.intake_item_id]);
  // Polls the document inventory while a document is still being read (see
  // `documentPolling.ts`); driven from the inventory and pending uploads below.
  const [documentPollingInterval, setDocumentPollingInterval] = useState(0);
  const {
    data: providerDocumentInventory,
    fulfilledTimeStamp: providerDocumentInventoryFetchedAt,
    refetch: refetchProviderDocumentInventory,
  } = useGetIntakeHubDocumentsQuery(
    { experience: 'v2' },
    {
      skip: isAdmin || !dashboard,
      pollingInterval: documentPollingInterval,
      skipPollingIfUnfocused: true,
    },
  );
  const {
    data: adminDocumentInventory,
    fulfilledTimeStamp: adminDocumentInventoryFetchedAt,
    refetch: refetchAdminDocumentInventory,
  } = useGetAdminIntakeHubDocumentsQuery(
    {
      organizationId: context.kind === 'admin' ? context.organizationId : '',
      userId: context.kind === 'admin' ? context.userId : undefined,
    },
    {
      skip: !isAdmin || !dashboard || isAggregateView,
      pollingInterval: documentPollingInterval,
      skipPollingIfUnfocused: true,
    },
  );
  const documentInventory = isAdmin ? adminDocumentInventory : providerDocumentInventory;
  const documentInventoryFetchedAt = isAdmin
    ? adminDocumentInventoryFetchedAt
    : providerDocumentInventoryFetchedAt;
  const refetchDocumentInventory = isAdmin
    ? refetchAdminDocumentInventory
    : refetchProviderDocumentInventory;
  const [generateProviderForm, { data: providerForm }] = useGenerateIntakeFormMutation();
  const [generateAdminForm, { data: adminForm }] = useGenerateAdminIntakeFormMutation();
  const [materializeProviderItems] = useMaterializeIntakeHubItemsMutation();
  const [materializeAdminItems] = useMaterializeAdminIntakeHubItemsMutation();
  const form = isAdmin ? adminForm : providerForm;
  const [updateProviderEvidence] = useUpdateIntakeHubItemEvidenceMutation();
  const [updateAdminEvidence] = useUpdateAdminIntakeHubItemEvidenceMutation();
  const [updateProviderConfiguration] = useUpdateIntakeFormConfigurationMutation();
  const [updateAdminConfiguration] = useUpdateAdminIntakeFormConfigurationMutation();
  const [updateSensitiveField] = useUpdateIntakeHubSensitiveFieldMutation();
  const [verifyAdminIntake, { isLoading: isVerifyingAdminIntake }] =
    useVerifyAdminIntakeHubMutation();
  const [discardAdminIntakeVerification, { isLoading: isDiscardingAdminVerification }] =
    useDiscardAdminIntakeHubVerificationMutation();
  const [acceptDocumentFieldSuggestions] = useAcceptIntakeHubDocumentFieldSuggestionsMutation();
  const storeAcceptedItems = useCallback(
    (items: GeneratedIntakeItem[]) => {
      setItemAuthorityState(previous => ({
        authority: items.reduce(
          (authority, item) => storeMutationItem(authority, item),
          previous.scope === itemAuthorityScope
            ? previous.authority
            : new Map<string, IntakeItemAuthorityEntry>(),
        ),
        scope: itemAuthorityScope,
      }));
    },
    [itemAuthorityScope],
  );
  const storeAcceptedItem = useCallback(
    (item: GeneratedIntakeItem) => storeAcceptedItems([item]),
    [storeAcceptedItems],
  );
  const writeQueue = useSerializedIntakeWrites();
  const acceptanceOperationTail = useRef<Promise<void>>(Promise.resolve());
  const acceptanceOperations = useRef(new Map<string, Promise<void>>());
  const acceptedSuggestionRequestIds = useRef(new Set<string>());
  const lastFormGenerationSignature = useRef<string | null>(null);
  const inFlightFormGenerationSignatures = useRef(new Set<string>());
  const automaticallyRetriedFormGenerationSignatures = useRef(new Set<string>());
  const currentFormGenerationSignature = useRef<string | null>(null);
  const [formGenerationRetrySequence, setFormGenerationRetrySequence] = useState(0);
  const materializationScope = itemAuthorityScope;
  const [materializationState, setMaterializationState] = useState<{
    scope: string;
    status: 'idle' | 'pending' | 'succeeded' | 'failed';
  }>(() => ({ scope: materializationScope, status: 'idle' }));
  const materializationStatus =
    materializationState.scope === materializationScope ? materializationState.status : 'idle';
  const currentMaterializationScope = useRef(materializationScope);
  const currentMaterializationAttempt = useRef<object | null>(null);
  if (currentMaterializationScope.current !== materializationScope) {
    currentMaterializationScope.current = materializationScope;
    currentMaterializationAttempt.current = null;
  }

  const runMaterialization = useCallback(async () => {
    if (isAggregateView || currentMaterializationAttempt.current) return;
    const attempt = {};
    currentMaterializationAttempt.current = attempt;
    const isCurrentAttempt = () =>
      currentMaterializationScope.current === materializationScope &&
      currentMaterializationAttempt.current === attempt;
    setMaterializationState({ scope: materializationScope, status: 'pending' });
    try {
      const response =
        context.kind === 'admin'
          ? await materializeAdminItems({
              organizationId: context.organizationId,
              userId: context.userId ?? '',
            }).unwrap()
          : await materializeProviderItems().unwrap();
      if (!isCurrentAttempt()) return;
      storeAcceptedItems(response.items ?? []);
      await Promise.all([refetchDashboard(), refetchDocumentInventory()]);
      if (!isCurrentAttempt()) return;
      setMaterializationState(previous =>
        previous.scope === materializationScope
          ? { scope: materializationScope, status: 'succeeded' }
          : previous,
      );
    } catch {
      if (!isCurrentAttempt()) return;
      setMaterializationState(previous =>
        previous.scope === materializationScope
          ? { scope: materializationScope, status: 'failed' }
          : previous,
      );
    } finally {
      if (currentMaterializationAttempt.current === attempt) {
        currentMaterializationAttempt.current = null;
      }
    }
  }, [
    context,
    isAggregateView,
    materializationScope,
    materializeAdminItems,
    materializeProviderItems,
    refetchDashboard,
    refetchDocumentInventory,
    storeAcceptedItems,
  ]);

  useEffect(() => {
    if (!dashboard || materializationStatus !== 'idle' || isAggregateView) return;
    void runMaterialization();
  }, [dashboard, isAggregateView, materializationStatus, runMaterialization]);
  const {
    triggerAutoSave: triggerProviderConfigurationSave,
    saveImmediatelyAndWait: saveProviderConfigurationImmediately,
    status: providerConfigurationSaveStatus,
    lastSavedAt: providerConfigurationLastSavedAt,
    hasPendingSave: hasPendingProviderConfigurationSave,
  } = useAutoSave<IntakeConfig>({
    enabled: !isAdmin && !isAggregateView && hydratedConfigurationScope === configurationScope,
    debounceMs: 800,
    flushOnUnmount: true,
    onSave: async nextConfiguration => {
      await updateProviderConfiguration({
        intakeFormConfigurationProviderUpdateRequest: intakeConfigurationUpdate(nextConfiguration),
      }).unwrap();
      setAcknowledgedConfig(nextConfiguration);
      // putV2ProviderIntakeHubConfig also invalidates the IntakeHubDashboard
      // tag (store/api.ts), so a config save can change profile_summary too —
      // refetch so handleCompletedPageContinue's awaited immediate save
      // doesn't advance the page before the headline is fresh.
      await refetchDashboard();
    },
  });
  // Mirrors the `enabled` condition above: while a provider's configuration hasn't
  // hydrated yet, saveProviderConfigurationImmediately (saveImmediatelyAndWait) rejects
  // instead of saving. A page that completes via that save (currently just F1) must
  // disable its Continue button during this window rather than let the click through
  // and surface a save-failure error for what's really just a loading state.
  const isProviderConfigurationHydrating =
    !isAdmin && hydratedConfigurationScope !== configurationScope;

  const organizationId = dashboard?.clinician?.organization_id;
  const userId = dashboard?.clinician?.user_id;
  const assignedProducts = useMemo<IntakeConfig['products']>(() => {
    const products = new Set<IntakeConfig['products'][number]>();
    const orderedProducts: IntakeConfig['products'] = ['jotcred', 'jotbill'];
    for (const enrollment of dashboard?.enrollments ?? []) {
      if (enrollment.service_key === 'jotcred' || enrollment.service_key === 'jotbill') {
        products.add(enrollment.service_key);
      }
    }
    return orderedProducts.filter(product => products.has(product));
  }, [dashboard?.enrollments]);
  const effectiveConfig = useMemo<IntakeConfig>(
    () => ({ ...config, products: assignedProducts }),
    [assignedProducts, config],
  );
  const shouldLoadEligibleProviders =
    effectiveConfig.practiceType === 'group' && effectiveConfig.role !== 'provider';
  // The clinician-role member list is only the candidate pool for the picker.
  // The roster itself is the explicit list stored in the Intake Hub tables.
  const {
    clinicians: providerRosterCandidatePool,
    isLoading: providerCandidatesLoading,
    error: providerCandidatesQueryError,
    refetch: refetchProviderCandidates,
  } = useClinicians({ skip: isAdmin || !shouldLoadEligibleProviders });
  const contextEligibleProviders = context.kind === 'admin' ? context.eligibleProviders : undefined;
  const rosterCandidatePool = useMemo(
    () => (isAdmin ? (contextEligibleProviders ?? []) : providerRosterCandidatePool),
    [contextEligibleProviders, isAdmin, providerRosterCandidatePool],
  );
  const providerRosterEntries = useMemo(
    () => dashboard?.provider_roster ?? [],
    [dashboard?.provider_roster],
  );
  const eligibleProviders = useMemo<EligibleProvider[]>(() => {
    const namesByUserId = new Map(
      rosterCandidatePool.map(candidate => [candidate.userId, candidate.name]),
    );
    return providerRosterEntries.map(entry => ({
      userId: entry.user_id,
      name: namesByUserId.get(entry.user_id) ?? entry.user_id,
      percentComplete: entry.progress?.percent_complete,
      status: entry.status ?? undefined,
      // journey_status is served by the roster payload; read it defensively so a
      // not-yet-regenerated API client still compiles.
      verifiedAt:
        (entry as { journey_status?: { verified_at?: string | null } }).journey_status
          ?.verified_at ?? null,
    }));
  }, [providerRosterEntries, rosterCandidatePool]);
  const rosterCandidates = useMemo(() => {
    const rosteredUserIds = new Set(providerRosterEntries.map(entry => entry.user_id));
    return rosterCandidatePool.filter(candidate => !rosteredUserIds.has(candidate.userId));
  }, [providerRosterEntries, rosterCandidatePool]);
  const eligibleProvidersLoading =
    context.kind === 'admin'
      ? false
      : providerCandidatesLoading || (isDashboardFetching && !dashboard);
  const eligibleProvidersQueryError =
    context.kind === 'admin' ? undefined : providerCandidatesQueryError;
  // Practice administrators manage their own org's intake roster from the
  // provider-facing wizard, not just JotPsych staff via the admin embed.
  // `manage_members` is granted to exactly the practice_administrator role
  // (ADR 0073 catalog) and is already the canonical "isPracticeAdmin" check
  // used elsewhere (e.g. CliniciansPage, useAddonUpsell).
  const canManageRosterAsPracticeAdmin = useCan('manage_members');
  const canManageRoster = isAdmin || canManageRosterAsPracticeAdmin;
  const [addProviderRosterEntry, addProviderRosterEntryResult] =
    useAddIntakeProviderRosterEntryMutation();
  const [removeProviderRosterEntry] = useRemoveIntakeProviderRosterEntryMutation();
  const [addAdminProviderRosterEntry, addAdminProviderRosterEntryResult] =
    useAddAdminIntakeProviderRosterEntryMutation();
  const [removeAdminProviderRosterEntry] = useRemoveAdminIntakeProviderRosterEntryMutation();
  const isAddingRosterProvider =
    addProviderRosterEntryResult.isLoading || addAdminProviderRosterEntryResult.isLoading;
  const handleAddRosterProvider = useCallback(
    (rosterUserId: string) => {
      if (context.kind === 'admin') {
        void addAdminProviderRosterEntry({
          organizationId: context.organizationId,
          intakeHubProviderRosterAddRequest: { user_id: rosterUserId },
        });
        return;
      }
      void addProviderRosterEntry({
        intakeHubProviderRosterAddRequest: { user_id: rosterUserId },
      });
    },
    [addAdminProviderRosterEntry, addProviderRosterEntry, context],
  );
  const handleRemoveRosterProvider = useCallback(
    (rosterUserId: string) => {
      if (context.kind === 'admin') {
        void removeAdminProviderRosterEntry({
          organizationId: context.organizationId,
          userId: rosterUserId,
        });
        return;
      }
      void removeProviderRosterEntry({ userId: rosterUserId });
    },
    [context, removeAdminProviderRosterEntry, removeProviderRosterEntry],
  );
  const hasUnsavedAdminChanges =
    isAdmin &&
    (!configurationsMatch(config, acknowledgedConfig) ||
      payerRosterDirty ||
      Object.values(fieldStates).some(
        state => JSON.stringify(state.draftValue) !== JSON.stringify(state.acknowledgedValue),
      ));

  const handleVerifyAdminIntake = useCallback(async () => {
    if (
      context.kind !== 'admin' ||
      !context.userId ||
      !canVerifyIntake ||
      hasUnsavedAdminChanges ||
      isSavingAdminChanges ||
      adminSaveErrorCount > 0
    )
      return;
    try {
      await verifyAdminIntake({
        organizationId: context.organizationId,
        userId: context.userId,
      }).unwrap();
      enqueueSnackbar('Intake verified.', { variant: 'success' });
    } catch {
      enqueueSnackbar('We could not verify this intake. Refresh and try again.', {
        variant: 'error',
      });
    }
  }, [
    adminSaveErrorCount,
    canVerifyIntake,
    context,
    enqueueSnackbar,
    hasUnsavedAdminChanges,
    isSavingAdminChanges,
    verifyAdminIntake,
  ]);

  const handleDiscardAdminVerification = useCallback(async () => {
    if (context.kind !== 'admin' || !context.userId || !canVerifyIntake) return;
    try {
      await discardAdminIntakeVerification({
        organizationId: context.organizationId,
        userId: context.userId,
      }).unwrap();
      enqueueSnackbar('Verification discarded.', { variant: 'success' });
    } catch {
      enqueueSnackbar('We could not discard this verification. Refresh and try again.', {
        variant: 'error',
      });
    }
  }, [canVerifyIntake, context, discardAdminIntakeVerification, enqueueSnackbar]);

  const discardAdminDrafts = useCallback(() => {
    setFieldStates(previous =>
      Object.fromEntries(
        Object.entries(previous).map(([fieldId, state]) => [
          fieldId,
          { ...state, draftValue: state.acknowledgedValue },
        ]),
      ),
    );
    setConfig(acknowledgedConfig);
    setPayerRosterDraft(acknowledgedPayerRoster);
    setAdminSaveErrorCount(0);
    setAdminSaveErrorMessage(undefined);
    setCompletionErrorPageKey(null);
  }, [acknowledgedConfig, acknowledgedPayerRoster]);

  const navigateToPage = useCallback(
    (pageKey: string, replace = false) => {
      if (isAdmin) {
        if (
          hasUnsavedAdminChanges &&
          !window.confirm('Discard unsaved Intake Hub changes and continue?')
        ) {
          return;
        }
        if (hasUnsavedAdminChanges) discardAdminDrafts();
        const search = new URLSearchParams(location.search);
        const slug = intakePagePath(pageKey).split('/').at(-1) ?? 'key-questions';
        search.set('intakeStep', slug);
        navigate({ pathname: location.pathname, search: search.toString() }, { replace });
        return;
      }
      navigate(
        {
          pathname: intakePagePath(pageKey),
          search: location.search,
        },
        { replace },
      );
    },
    [
      discardAdminDrafts,
      hasUnsavedAdminChanges,
      isAdmin,
      location.pathname,
      location.search,
      navigate,
    ],
  );

  useEffect(() => {
    onDirtyChange?.(hasUnsavedAdminChanges);
  }, [hasUnsavedAdminChanges, onDirtyChange]);

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  useEffect(() => {
    if (!hasUnsavedAdminChanges) return undefined;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [hasUnsavedAdminChanges]);

  useEffect(() => {
    if (!routedPageKey) navigateToPage('K1', true);
  }, [navigateToPage, routedPageKey]);

  useEffect(() => {
    const defaults = defaultIntakeConfig();
    setConfig(defaults);
    setAcknowledgedConfig(defaults);
    setHydratedConfigurationScope(isAggregateView ? configurationScope : null);
    setAggregatePage(1);
  }, [configurationScope, isAggregateView]);

  useEffect(() => {
    if (!serverConfiguration) return;
    const hydrated = serverConfigurationToIntakeConfig(serverConfiguration);
    setConfig(previous => (configurationsMatch(previous, hydrated) ? previous : hydrated));
    setAcknowledgedConfig(previous =>
      configurationsMatch(previous, hydrated) ? previous : hydrated,
    );
    setHydratedConfigurationScope(configurationScope);
  }, [configurationScope, serverConfiguration]);

  useEffect(() => {
    if (
      assignedProducts.length === 0 ||
      hydratedConfigurationScope !== configurationScope ||
      configurationError
    )
      return;
    const generateIntakeFormRequest = {
      role: effectiveConfig.role,
      products: effectiveConfig.products,
      practice_type: effectiveConfig.practiceType,
      is_prescriber: effectiveConfig.isPrescriber,
      license_types: effectiveConfig.licenseTypes,
      payers: payerRosterDraft.map(payer => ({
        display_name: payer.payer_name,
        primary_payer_id: payer.payer_id,
        states: [],
        plan_types: [],
      })),
    };
    const generationSignature = JSON.stringify({
      configurationScope,
      generateIntakeFormRequest,
    });
    currentFormGenerationSignature.current = generationSignature;
    if (
      generationSignature === lastFormGenerationSignature.current ||
      inFlightFormGenerationSignatures.current.has(generationSignature)
    )
      return;
    inFlightFormGenerationSignatures.current.add(generationSignature);
    let generationRequest;
    if (context.kind === 'admin') {
      if (!context.userId) {
        inFlightFormGenerationSignatures.current.delete(generationSignature);
        return;
      }
      generationRequest = generateAdminForm({
        organizationId: context.organizationId,
        userId: context.userId,
        generateIntakeFormRequest,
      });
    } else {
      generationRequest = generateProviderForm({ generateIntakeFormRequest });
    }
    let generationFailed = false;
    void generationRequest
      .unwrap()
      .then(() => {
        if (currentFormGenerationSignature.current === generationSignature) {
          lastFormGenerationSignature.current = generationSignature;
        }
        automaticallyRetriedFormGenerationSignatures.current.delete(generationSignature);
      })
      .catch(() => {
        generationFailed = true;
      })
      .finally(() => {
        inFlightFormGenerationSignatures.current.delete(generationSignature);
        if (
          generationFailed &&
          currentFormGenerationSignature.current === generationSignature &&
          !automaticallyRetriedFormGenerationSignatures.current.has(generationSignature)
        ) {
          automaticallyRetriedFormGenerationSignatures.current.add(generationSignature);
          setFormGenerationRetrySequence(sequence => sequence + 1);
        }
      });
  }, [
    assignedProducts.length,
    configurationError,
    configurationScope,
    context,
    effectiveConfig,
    formGenerationRetrySequence,
    generateAdminForm,
    generateProviderForm,
    hydratedConfigurationScope,
    payerRosterDraft,
  ]);

  const allFields = useMemo(
    () =>
      activeFormFields(
        (form?.canvases ?? [])
          .filter(canvas => canvas.is_active)
          .flatMap(canvas => canvas.fields ?? []),
      ),
    [form],
  );
  const hasMissingMaterializedDestinations = useMemo(
    () =>
      !isAggregateView &&
      allFields.some(field => {
        if (field.is_writable === false) return false;
        if (
          field.destination.kind !== 'intake_item_evidence' &&
          field.destination.kind !== 'intake_item_sensitive'
        ) {
          return false;
        }
        return (
          !field.requirement_key ||
          !findIntakeItemForField(field, dashboard?.items ?? [], userId)?.intake_item_id
        );
      }),
    [allFields, dashboard?.items, isAggregateView, userId],
  );
  const isPreparingMissingItems =
    materializationStatus === 'pending' && hasMissingMaterializedDestinations;
  const fieldPageIndex = useMemo(() => {
    const index: Record<string, string> = {};
    for (const canvas of form?.canvases ?? []) {
      if (!canvas.is_active || !canvas.canvas_key) continue;
      for (const field of canvas.fields ?? []) index[field.field_id] = canvas.canvas_key;
    }
    return index;
  }, [form]);
  const serverRepeatRecordsByGroup = useMemo(() => {
    const grouped = new Map<string, GeneratedIntakeField[]>();
    for (const field of allFields) {
      if (!field.repeat_scope || field.repeat_scope === 'none' || !field.requirement_key) continue;
      const key = repeatGroupKey(field);
      grouped.set(key, [...(grouped.get(key) ?? []), field]);
    }
    return Object.fromEntries(
      [...grouped.entries()].map(([key, fields]) => {
        const item = findIntakeItemForField(fields[0], dashboard?.items ?? [], userId);
        const extracted = item?.evidence_summary?.extracted_fields;
        return [
          key,
          hydrateRepeatRecords(
            extracted && typeof extracted === 'object' ? extracted : {},
            fields.map(field => field.destination.field_key),
            fields[0].requirement_key ?? fields[0].group_id,
          ),
        ];
      }),
    );
  }, [allFields, dashboard?.items, userId]);
  const [repeatGroupStates, setRepeatGroupStates] = useState<IntakeRepeatGroupStates>({});
  useEffect(() => {
    setRepeatGroupStates(previous =>
      reconcileRepeatGroupStates(serverRepeatRecordsByGroup, previous),
    );
  }, [serverRepeatRecordsByGroup]);
  const repeatRecordsByGroup = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(serverRepeatRecordsByGroup).map(([groupKey, serverRecords]) => [
          groupKey,
          repeatGroupStates[groupKey]?.draftRecords ?? serverRecords,
        ]),
      ),
    [repeatGroupStates, serverRepeatRecordsByGroup],
  );
  const acknowledgedRepeatRecordsByGroup = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(serverRepeatRecordsByGroup).map(([groupKey, serverRecords]) => [
          groupKey,
          repeatGroupStates[groupKey]?.acknowledgedRecords ?? serverRecords,
        ]),
      ),
    [repeatGroupStates, serverRepeatRecordsByGroup],
  );
  const activeSuggestionFieldsById = useMemo(
    () =>
      Object.fromEntries(
        allFields.filter(canDisplayDocumentSuggestion).map(field => [field.field_id, field]),
      ),
    [allFields],
  );
  const activeSuggestionFieldIds = useMemo(
    () => Object.keys(activeSuggestionFieldsById),
    [activeSuggestionFieldsById],
  );
  const { documentSuggestionReferenceIds, documentSuggestions } = useMemo(() => {
    const indexed = new Map<string, IntakeFieldSuggestion>();
    const referenceIds = new Map<string, string>();
    const conflicts = new Set<string>();
    if (isAdmin) return { documentSuggestionReferenceIds: {}, documentSuggestions: {} };
    for (const documentItem of documentInventory?.documents ?? []) {
      for (const suggestion of documentItem.field_suggestions ?? []) {
        const field = activeSuggestionFieldsById[suggestion.field_id];
        if (!field) continue;
        const fieldState = fieldStates[suggestion.field_id];
        const isRepeated = Boolean(field.repeat_scope && field.repeat_scope !== 'none');
        const repeatedDraftConflict = (() => {
          if (!isRepeated) return false;
          if (!suggestion.repeat_record_id) return true;
          const groupKey = repeatGroupKey(field);
          const draftMatches = (repeatRecordsByGroup[groupKey] ?? []).filter(
            record => record.record_id === suggestion.repeat_record_id,
          );
          const acknowledgedMatches = (acknowledgedRepeatRecordsByGroup[groupKey] ?? []).filter(
            record => record.record_id === suggestion.repeat_record_id,
          );
          // A suggestion may name a record the server has not created yet — it
          // creates one lazily, on the first accepted suggestion for a
          // document. Nothing local can conflict with a record that does not
          // exist, so let it through to render on a placeholder entry.
          // Practice address is excluded: it keeps its primary-record-only
          // rule, so such a suggestion would be counted here and then dropped
          // by the group, inflating the "landed" summary with nothing to show.
          if (
            field.requirement_key !== PRACTICE_ADDRESS_REQUIREMENT_KEY &&
            draftMatches.length === 0 &&
            acknowledgedMatches.length === 0
          )
            return false;
          if (draftMatches.length !== 1 || acknowledgedMatches.length !== 1) return true;
          const fieldKey = field.destination.field_key;
          const draftValue = draftMatches[0].values[fieldKey];
          const acknowledgedValue = acknowledgedMatches[0].values[fieldKey];
          return (
            JSON.stringify(draftValue) !== JSON.stringify(acknowledgedValue) ||
            JSON.stringify(draftValue) === JSON.stringify(suggestion.value)
          );
        })();
        // Repeated suggestions are indexed per record, so a second uploaded
        // licence does not cancel the first's suggestion for the same field.
        const entryKey = suggestionEntryKey(suggestion.field_id, suggestion.repeat_record_id);
        if (
          conflicts.has(entryKey) ||
          discardedSuggestionIds.has(suggestionIdentity(suggestion)) ||
          repeatedDraftConflict ||
          (!isRepeated &&
            fieldState &&
            intakeFieldValuesEqual(fieldState.draftValue, suggestion.value))
        )
          continue;
        const existing = indexed.get(entryKey);
        if (existing && JSON.stringify(existing.value) !== JSON.stringify(suggestion.value)) {
          indexed.delete(entryKey);
          referenceIds.delete(entryKey);
          conflicts.add(entryKey);
          continue;
        }
        indexed.set(entryKey, suggestion);
        referenceIds.set(entryKey, documentItem.document_reference.document_reference_id);
      }
    }
    return {
      documentSuggestionReferenceIds: Object.fromEntries(referenceIds),
      documentSuggestions: Object.fromEntries(indexed),
    };
  }, [
    acknowledgedRepeatRecordsByGroup,
    activeSuggestionFieldsById,
    discardedSuggestionIds,
    documentInventory?.documents,
    fieldStates,
    isAdmin,
    repeatRecordsByGroup,
  ]);
  const suggestionsByPage = useMemo(() => {
    const counts: Record<string, { total: number; unconfirmed: number }> = {};
    for (const suggestion of Object.values(documentSuggestions)) {
      const pageKey = fieldPageIndex[suggestion.field_id];
      if (!pageKey) continue;
      const count = counts[pageKey] ?? { total: 0, unconfirmed: 0 };
      count.total += 1;
      count.unconfirmed += 1;
      counts[pageKey] = count;
    }
    return counts;
  }, [documentSuggestions, fieldPageIndex]);
  const landedSummary = useMemo(() => {
    const parts = ORDERED_KEYS.flatMap(pageKey => {
      const count = suggestionsByPage[pageKey]?.unconfirmed ?? 0;
      return count > 0 ? [`${count} field${count === 1 ? '' : 's'} on ${pageLabel(pageKey)}`] : [];
    });
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [suggestionsByPage]);
  const firstPageWithSuggestions = useMemo(
    () => ORDERED_KEYS.find(pageKey => (suggestionsByPage[pageKey]?.unconfirmed ?? 0) > 0) ?? null,
    [suggestionsByPage],
  );
  useEffect(() => {
    const confirmedSensitiveFields = new Set<string>();
    const fieldsToHydrate = allFields.filter(field => {
      if (
        !pendingSensitiveConfirmations.has(field.field_id) ||
        field.destination.kind !== 'intake_item_sensitive'
      ) {
        return true;
      }
      const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
      const isConfirmed = Boolean(
        item?.sensitive_fields_on_file?.includes(field.destination.field_key),
      );
      if (isConfirmed) confirmedSensitiveFields.add(field.field_id);
      return isConfirmed;
    });
    setFieldStates(previous =>
      hydrateFieldStates(fieldsToHydrate, dashboard?.items ?? [], previous, userId),
    );
    if (confirmedSensitiveFields.size > 0) {
      setPendingSensitiveConfirmations(previous => {
        const next = new Set(previous);
        for (const fieldId of confirmedSensitiveFields) next.delete(fieldId);
        return next;
      });
    }
  }, [allFields, dashboard?.items, pendingSensitiveConfirmations, userId]);

  // ---- uploads -----------------------------------------------------------
  const { constraints: providerUploadConstraints } = useArtifactUploadConstraints();
  const { constraints: adminUploadConstraints } = useAdminArtifactUploadConstraints();
  const constraints = isAdmin ? adminUploadConstraints : providerUploadConstraints;
  const { upload: uploadProviderFiles } = useUploadOrganizationArtifacts(organizationId);
  const { upload: uploadAdminFiles } = useUploadAdminOrganizationArtifacts(
    context.kind === 'admin' ? context.organizationId : '',
  );
  const {
    pendingUploads,
    trackUpload,
    dismissByArtifactId,
    reconcile,
    handleArtifactEvent,
    handleDocRefExtraction,
  } = useOptimisticUploads({ retainUntilReconciled: true });
  const [uploadingFileCount, setUploadingFileCount] = useState(0);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!organizationId || isAggregateView) return;
      if (!userId) {
        enqueueSnackbar('Select a provider before uploading intake documents.', {
          variant: 'warning',
        });
        return;
      }
      const { accepted } = validateFiles(files, constraints);
      if (accepted.length === 0) return;
      const upload = isAdmin ? uploadAdminFiles : uploadProviderFiles;
      setUploadingFileCount(accepted.length);
      let outcome: Awaited<ReturnType<typeof upload>>;
      try {
        outcome = await upload(accepted, {
          sourceChannel: isAdmin ? 'intake_hub_admin' : 'intake_hub',
          intakeHub:
            userId && form?.catalog_version
              ? {
                  userId,
                  catalogVersion: form.catalog_version,
                  activeFieldIds: activeSuggestionFieldIds,
                }
              : undefined,
        });
      } catch (error) {
        enqueueSnackbar(formatUploadLimitError(error), { variant: 'error' });
        return;
      } finally {
        setUploadingFileCount(0);
      }
      if (outcome) {
        trackUpload(outcome);
        void refetchDocumentInventory();
      }
    },
    [
      activeSuggestionFieldIds,
      constraints,
      enqueueSnackbar,
      form?.catalog_version,
      isAdmin,
      isAggregateView,
      organizationId,
      refetchDocumentInventory,
      trackUpload,
      uploadAdminFiles,
      uploadProviderFiles,
      userId,
    ],
  );

  const { isDragging } = useChartWideDropZone({
    enabled:
      !isAggregateView &&
      Boolean(organizationId) &&
      (currentKey in DOCUMENT_PAGES || currentKey === 'M6' || currentKey === 'P6'),
    onDrop: files => {
      void handleFiles(files);
    },
  });
  const isUploadInFlight = uploadingFileCount > 0;

  const handleArtifactStreamEvent = useCallback(
    (event: Parameters<typeof handleArtifactEvent>[0]) => {
      handleArtifactEvent(event);
      void refetchDocumentInventory();
    },
    [handleArtifactEvent, refetchDocumentInventory],
  );

  useOrganizationArtifactEventStream(organizationId, {
    enabled: !isAdmin && Boolean(organizationId),
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetchDocumentInventory();
      void refetchDashboard();
      const summary = event.summary ?? {};
      const artifactId = event.artifactId ?? '';
      if (artifactId) {
        setDocKinds(prev => ({
          ...prev,
          [artifactId]: {
            docKind: (summary as { doc_kind?: string }).doc_kind ?? null,
            filled:
              summary.extracted_fields && typeof summary.extracted_fields === 'object'
                ? Object.keys(summary.extracted_fields).length
                : 0,
          },
        }));
      }
    },
  });
  useAdminOrganizationArtifactEventStream(context.kind === 'admin' ? context.organizationId : '', {
    enabled: isAdmin && Boolean(organizationId),
    onEvent: handleArtifactStreamEvent,
    onDocRefExtraction: event => {
      handleDocRefExtraction(event);
      void refetchDocumentInventory();
      void refetchDashboard();
    },
  });

  useEffect(() => {
    reconcile(
      (documentInventory?.documents ?? []).map(documentItem => documentItem.document_reference),
    );
  }, [documentInventory?.documents, reconcile]);

  // ---- derived -----------------------------------------------------------
  const pageStats = useMemo(() => {
    const stats: Record<string, { fieldCount: number; requiredCount: number; active: boolean }> =
      {};
    for (const canvas of form?.canvases ?? []) {
      stats[canvas.canvas_key ?? ''] = {
        fieldCount: canvas.fields?.length ?? 0,
        requiredCount: canvas.required_count ?? 0,
        active: canvas.is_active,
      };
    }
    return stats;
  }, [form]);

  const notNeededKeys = useMemo(() => {
    if (isAggregateView) return new Set<string>();
    if (!form) return new Set<string>();
    // A trailing canvas whose fields this surface cannot persist (all uploads,
    // or all read-only) would render an empty page — IntakeFieldsPage drops
    // `file` fields — so it is "not needed" here even though the backend marks
    // it active. Same predicate that picks the completion-page labels.
    const trailingWithoutAnswerableFields = new Set(
      (form.canvases ?? [])
        .filter(
          canvas =>
            canvas.section === TRAILING_SECTION &&
            canvas.canvas_key &&
            !activeFormFields(canvas.fields ?? []).some(isPersistableIntakeField),
        )
        .flatMap(canvas => (canvas.canvas_key ? [canvas.canvas_key] : [])),
    );
    return new Set(
      ORDERED_KEYS.filter(
        key =>
          !['K1', 'F1', 'X1'].includes(key) &&
          (!pageStats[key]?.active || trailingWithoutAnswerableFields.has(key)),
      ),
    );
  }, [form, isAggregateView, pageStats]);

  useEffect(() => {
    if (isAggregateView) return;
    if (!form || !routedPageKey || ['K1', 'F1', 'X1'].includes(routedPageKey)) return;
    // Existence only. A canvas the customer does not need still exists in the
    // generated form, so "needed" is a separate question — see the trailing
    // guard below, which needs completionPageReachable to pick a destination.
    const pageExists = (form.canvases ?? []).some(canvas => canvas.canvas_key === routedPageKey);
    if (!pageExists) navigateToPage('K1', true);
  }, [form, isAggregateView, navigateToPage, routedPageKey]);

  useEffect(() => {
    // F1 stays reachable while the gate is closed, matching IntakeShell's
    // unlock rule and the stale-route guard above.
    if (!serverConfiguration || gateOpen || ['K1', 'F1'].includes(currentKey)) return;
    if (currentKey === 'X1' && adminVerificationNeedsCompletionPage) return;
    navigateToPage('K1', true);
  }, [
    adminVerificationNeedsCompletionPage,
    currentKey,
    gateOpen,
    navigateToPage,
    serverConfiguration,
  ]);

  const payerRows = useMemo(
    () =>
      payerRosterDraft.map(
        (payer): PayerRow => ({
          displayName: payer.payer_name,
          enrollmentStatus: payer.enrollment_status ?? 'already_enrolled',
          jotbill: payer.jotbill ?? false,
          jotcred: payer.jotcred ?? false,
          payerId: payer.payer_id,
          rowId: payer.payer_id,
        }),
      ),
    [payerRosterDraft],
  );

  const derivedSentence = useMemo(() => {
    const flags = form?.derived_flags;
    if (!flags) return null;
    const parts: string[] = [];
    if (flags.has_medicare && flags.has_medicaid)
      parts.push("Medicare and Medicaid detected → we'll ask for a PTAN and a Medicaid ID later.");
    else if (flags.has_medicare) parts.push("Medicare detected → we'll ask for a PTAN later.");
    else if (flags.has_medicaid)
      parts.push("Medicaid detected → we'll ask for a Medicaid ID later.");
    if (flags.payer_requires_references)
      parts.push('A selected payer needs professional references before we can file there.');
    if (flags.payer_requires_portal_login) parts.push('A selected payer needs a portal login.');
    return parts.length > 0 ? parts.join(' ') : null;
  }, [form]);

  const totalRequired = useMemo(
    () =>
      (form?.canvases ?? [])
        .filter(canvas => canvas.is_active)
        .reduce((sum, canvas) => sum + (canvas.required_count ?? 0), 0),
    [form],
  );

  // Same object shape (and construction) as `foundDocuments`, keyed by
  // `document_reference_id` so a satisfied forecast tile / requirement row can
  // resolve the exact FoundDocument the "Documents received" list would
  // render for it, and open it through the same preview dialog via
  // `handleOpenDocument`.
  const decomposedReferenceIds = useMemo(
    () => decomposedDocumentReferenceIds(documentInventory?.documents ?? []),
    [documentInventory?.documents],
  );

  const documentByReferenceId = useMemo(
    () =>
      Object.fromEntries(
        (documentInventory?.documents ?? []).map(documentItem => [
          documentItem.document_reference.document_reference_id,
          persistedDocumentToFoundDocument(documentItem, allFields, decomposedReferenceIds),
        ]),
      ),
    [documentInventory?.documents, allFields, decomposedReferenceIds],
  );

  // The display name for a document a repeat record is linked to, so an entry
  // can say which upload backs it.
  const documentNameByReferenceId = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(documentByReferenceId).map(([referenceId, document]) => [
          referenceId,
          document.filename,
        ]),
      ),
    [documentByReferenceId],
  );

  const documentsToGather: ForecastDocument[] = useMemo(() => {
    const activeDocumentFields = activeFormFields(
      (form?.canvases ?? [])
        .filter(
          canvas =>
            canvas.is_active &&
            canvas.canvas_type === 'upload' &&
            ['clinic_setup', 'your_intake'].includes(canvas.section),
        )
        .flatMap(canvas => canvas.fields ?? []),
    ).filter(
      field => field.data_type === 'file' && field.destination.kind === 'document_reference',
    );
    const grouped = new Map<
      string,
      ForecastDocument & {
        forecastOrder: number;
        sourceIndex: number;
        fields: GeneratedIntakeField[];
      }
    >();
    activeDocumentFields.forEach((field, sourceIndex) => {
      const presentation = field.document_forecast;
      const groupKey = presentation?.group_key ?? `field:${field.field_id}`;
      const existing = grouped.get(groupKey);
      if (existing) {
        existing.fields.push(field);
        return;
      }
      grouped.set(groupKey, {
        fieldId: groupKey,
        label: presentation?.label ?? field.label,
        detail: presentation?.detail ?? (field.help_text || field.condition || undefined),
        forecastOrder: presentation?.order ?? Number.MAX_SAFE_INTEGER,
        sourceIndex,
        fields: [field],
      });
    });
    return [...grouped.values()]
      .sort(
        (left, right) =>
          left.forecastOrder - right.forecastOrder || left.sourceIndex - right.sourceIndex,
      )
      .map(({ fieldId, label, detail, fields }) => {
        // Absent dashboard data (e.g. F1 rendered before the form/items load) resolves every
        // field to "no backing item", so groupSatisfaction naturally reports unsatisfied — the
        // tile just stays neutral rather than erroring.
        const { satisfied, satisfiedByFilename, satisfiedByAdditionalCount } = groupSatisfaction(
          fields,
          dashboard?.items ?? [],
          userId,
          documentByReferenceId,
        );
        return {
          fieldId,
          label,
          detail,
          satisfied,
          satisfiedByFilename,
          satisfiedByAdditionalCount,
        };
      });
  }, [form, dashboard?.items, userId, documentByReferenceId]);

  // Headline percent is the backend item-based profile_summary — the same
  // number every admin/staff surface shows for this provider — never the
  // form-field completion metric, which can diverge materially from it (a
  // provider read 55% while the admin roster showed 12%). Field-based
  // completion still drives the per-page rail states below.
  const percentComplete = dashboard?.profile_summary?.percent_complete ?? 0;
  const { completionByPage, progressByPage } = useMemo(() => {
    const progress: Record<string, RailProgressState> = {};
    const completion: Record<string, boolean> = {};
    const completedPages = new Set(acknowledgedConfig.completedPages);
    for (const canvas of form?.canvases ?? []) {
      if (!canvas.is_active || !canvas.canvas_key) continue;
      // Retired-catalog fields (see RETIRED_GROUP_IDS) never count toward a
      // canvas's completion — a stale backend still serving one must not
      // block completion or the F1/K1 rail state.
      const canvasFields = activeFormFields(canvas.fields ?? []);
      if (canvas.canvas_key === 'F1') {
        progress.F1 = completedPages.has('F1') ? 'completed' : 'notStarted';
        completion.F1 = completedPages.has('F1');
        continue;
      }
      if (canvas.canvas_type === 'summary') continue;
      if (canvas.canvas_key === 'K1') {
        const hasUnsavedConfiguration = !configurationsMatch(config, acknowledgedConfig);
        progress.K1 =
          hasUnsavedConfiguration || payerRosterDirty
            ? 'inProgress'
            : gateOpen
              ? 'completed'
              : 'notStarted';
        completion.K1 = gateOpen && !hasUnsavedConfiguration && !payerRosterDirty;
        continue;
      }
      if (canvas.canvas_key === 'M5') {
        progress.M5 =
          providerRosterEntries.length > 0 && completedPages.has('M5')
            ? 'completed'
            : providerRosterEntries.length > 0
              ? 'inProgress'
              : 'notStarted';
        completion.M5 = providerRosterEntries.length === 0 || progress.M5 === 'completed';
        continue;
      }
      // Trailing canvases carry only optional fields, so
      // durableRequiredCompletion reports total 0 and its `complete` (which
      // needs total > 0) could never be true — the rail dot would stay blue
      // however much the provider filled in. Count every answerable field
      // instead, through the same repeat-aware maths: repeat-scoped values
      // live in repeatRecordsByGroup, and only the first record is mirrored
      // into the flat field keys `fieldStates` is built from, so a per-field
      // read of `fieldStates` would call a group done on one record and never
      // notice later ones added or removed. `completion` stays true
      // regardless: trailing items never gate the review pages or X1.
      if (canvas.section === TRAILING_SECTION) {
        const answerable = durableAnswerableCompletion(
          canvasFields,
          fieldStates,
          dashboard?.items ?? [],
          userId,
          acknowledgedRepeatRecordsByGroup,
        );
        const hasDirtyTrailingFields = canvasFields.some(field => {
          const state = fieldStates[field.field_id];
          return state && !intakeFieldValuesEqual(state.draftValue, state.acknowledgedValue);
        });
        const hasDirtyTrailingRepeatGroups = canvasFields.some(field => {
          if (!field.repeat_scope || field.repeat_scope === 'none') return false;
          const groupKey = repeatGroupKey(field);
          return !repeatRecordsEqual(
            repeatRecordsByGroup[groupKey] ?? [],
            acknowledgedRepeatRecordsByGroup[groupKey] ?? [],
          );
        });
        // A saved-but-incomplete answer is progress even though it counts as
        // zero complete — including a repeat group with a half-filled record,
        // whose first record is mirrored into the flat field keys.
        const hasAcknowledgedTrailingFields = canvasFields.some(
          field => fieldStates[field.field_id]?.hasAcknowledgedValue,
        );
        const isDirty = hasDirtyTrailingFields || hasDirtyTrailingRepeatGroups;
        progress[canvas.canvas_key] =
          answerable.total > 0 && answerable.complete === answerable.total && !isDirty
            ? 'completed'
            : answerable.complete > 0 || isDirty || hasAcknowledgedTrailingFields
              ? 'inProgress'
              : 'notStarted';
        completion[canvas.canvas_key] = true;
        continue;
      }
      if (canvas.canvas_type === 'upload') {
        const requiredFields = requiredDocumentFields(canvasFields);
        const requiredItems = new Map(
          requiredFields.map(field => {
            const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
            return [
              item?.intake_item_id ?? `${field.view}:${field.requirement_key ?? field.field_id}`,
              item,
            ];
          }),
        );
        const complete = [...requiredItems.values()].filter(item => item?.is_complete).length;
        progress[canvas.canvas_key] =
          requiredItems.size === 0
            ? 'notStarted'
            : complete === requiredItems.size
              ? 'completed'
              : complete > 0
                ? 'inProgress'
                : 'notStarted';
        completion[canvas.canvas_key] = requiredItems.size === 0 || complete === requiredItems.size;
        continue;
      }
      const durableCompletion = durableRequiredCompletion(
        canvasFields,
        fieldStates,
        dashboard?.items ?? [],
        userId,
        acknowledgedRepeatRecordsByGroup,
      );
      const draftStates = Object.fromEntries(
        canvasFields.flatMap(field => {
          const state = fieldStates[field.field_id];
          if (!state) return [];
          const draftIsDirty = !intakeFieldValuesEqual(state.draftValue, state.acknowledgedValue);
          return [
            [
              field.field_id,
              {
                ...state,
                acknowledgedValue: state.draftValue,
                hasAcknowledgedValue: state.hasAcknowledgedValue || draftIsDirty,
              },
            ],
          ];
        }),
      );
      const draftCompletion = durableRequiredCompletion(
        canvasFields,
        draftStates,
        dashboard?.items ?? [],
        userId,
        repeatRecordsByGroup,
      );
      const hasDirtyFields = canvasFields.some(field => {
        const state = fieldStates[field.field_id];
        return state && !intakeFieldValuesEqual(state.draftValue, state.acknowledgedValue);
      });
      const hasDirtyRepeatGroups = canvasFields.some(field => {
        if (!field.repeat_scope || field.repeat_scope === 'none') return false;
        const groupKey = repeatGroupKey(field);
        return !repeatRecordsEqual(
          repeatRecordsByGroup[groupKey] ?? [],
          acknowledgedRepeatRecordsByGroup[groupKey] ?? [],
        );
      });
      const hasAcknowledgedFields = canvasFields.some(
        field => fieldStates[field.field_id]?.hasAcknowledgedValue,
      );
      const complete =
        durableCompletion.total > 0 &&
        !hasDirtyFields &&
        !hasDirtyRepeatGroups &&
        durableCompletion.complete === durableCompletion.total &&
        draftCompletion.complete === draftCompletion.total;
      const hasProgress =
        hasDirtyFields ||
        hasDirtyRepeatGroups ||
        hasAcknowledgedFields ||
        durableCompletion.complete > 0 ||
        draftCompletion.complete > 0;
      progress[canvas.canvas_key] = complete
        ? 'completed'
        : hasProgress
          ? 'inProgress'
          : 'notStarted';
      completion[canvas.canvas_key] = durableCompletion.total === 0 || complete;
    }

    // F1 ("What we'll need") is a fixed rail page, not a generated canvas, so
    // it's never visited by the loop above in production — its completion state
    // lives only in acknowledgedConfig.completedPages. The `progress.F1 === undefined`
    // guard only backfills that missing case: if a canvas keyed 'F1' ever *does* appear
    // (as today's tests synthesize), the loop's own branch already set progress.F1 and
    // completion.F1, and this must not clobber that value.
    if (progress.F1 === undefined) {
      progress.F1 = completedPages.has('F1') ? 'completed' : 'notStarted';
      completion.F1 = completedPages.has('F1');
    }

    for (const reviewPage of [
      { pageKey: 'M6', sectionKey: 'clinic_setup' },
      { pageKey: 'P6', sectionKey: 'your_intake' },
    ] as const) {
      const reviewCanvas = (form?.canvases ?? []).find(
        canvas =>
          canvas.is_active &&
          canvas.canvas_type === 'summary' &&
          canvas.canvas_key === reviewPage.pageKey,
      );
      if (!reviewCanvas) continue;
      const sectionPageKeys = (form?.canvases ?? [])
        .filter(
          canvas =>
            canvas.is_active &&
            canvas.canvas_type !== 'summary' &&
            canvas.section === reviewPage.sectionKey &&
            Boolean(canvas.canvas_key),
        )
        .flatMap(canvas => (canvas.canvas_key ? [canvas.canvas_key] : []))
        .filter(pageKey => !notNeededKeys.has(pageKey));
      const sectionComplete =
        sectionPageKeys.length > 0 && sectionPageKeys.every(pageKey => completion[pageKey]);
      const sectionStarted = sectionPageKeys.some(
        pageKey => progress[pageKey] !== undefined && progress[pageKey] !== 'notStarted',
      );
      progress[reviewPage.pageKey] =
        sectionComplete && completedPages.has(reviewPage.pageKey)
          ? 'completed'
          : sectionStarted
            ? 'inProgress'
            : 'notStarted';
      completion[reviewPage.pageKey] = sectionComplete && completedPages.has(reviewPage.pageKey);
    }
    return { completionByPage: completion, progressByPage: progress };
  }, [
    acknowledgedConfig,
    acknowledgedRepeatRecordsByGroup,
    config,
    dashboard?.items,
    fieldStates,
    form?.canvases,
    gateOpen,
    notNeededKeys,
    payerRosterDirty,
    providerRosterEntries.length,
    repeatRecordsByGroup,
    userId,
  ]);
  const activeCompletionPageKeys = useMemo(
    () =>
      (form?.canvases ?? [])
        .filter(canvas => {
          if (!canvas.is_active || !canvas.canvas_key) return false;
          if (!['clinic_setup', 'your_intake'].includes(canvas.section)) return false;
          const fields = canvas.fields ?? [];
          // A canvas whose fields are entirely retired-group (e.g. a stale
          // backend still serving I23 on M4 with the checkbox UI gone) has
          // no control left to satisfy it, so it must not gate completion.
          if (fields.length > 0 && fields.every(isRetiredGroupField)) return false;
          return true;
        })
        .flatMap(canvas => (canvas.canvas_key ? [canvas.canvas_key] : [])),
    [form?.canvases],
  );
  const isCompletionPageComplete = useCallback(
    (pageKey: string) => completionByPage[pageKey] ?? false,
    [completionByPage],
  );
  // The trailing phase: reachable whenever the generated form carries an active
  // post-critical canvas and the K1 gate is open. It is deliberately not
  // locked behind the critical pages — it gates nothing and is gated by
  // nothing.
  const activeTrailingCanvases = useMemo(
    () =>
      (form?.canvases ?? []).filter(
        canvas =>
          canvas.is_active &&
          canvas.section === TRAILING_SECTION &&
          TRAILING_PAGE_KEYS.includes(canvas.canvas_key ?? '') &&
          // Same rule the rail uses: a canvas with nothing this surface can
          // persist is not offered, and must not be promised either.
          activeFormFields(canvas.fields ?? []).some(isPersistableIntakeField),
      ),
    [form?.canvases],
  );
  const trailingPageKey = activeTrailingCanvases[0]?.canvas_key ?? null;
  // Only name a group this page can actually collect. Upload-only groups (D12
  // payer approval letters, D10 fee schedule) carry nothing but
  // document_reference fields, which IntakeFieldsPage drops from its render —
  // naming one would promise an item the CTA lands on a page that cannot take.
  // The predicate is `answerableTrailingFields`, the same one progress and
  // `trailingItemsRemain` use, so the copy and the rail can never disagree.
  const trailingItemLabels = useMemo(
    () => [
      ...new Set(
        activeTrailingCanvases.flatMap(canvas => {
          const answerableGroupIds = new Set(
            activeFormFields(canvas.fields ?? [])
              .filter(isPersistableIntakeField)
              .map(field => field.group_id),
          );
          return (canvas.groups ?? []).flatMap(group =>
            group.group_name && answerableGroupIds.has(group.group_id) ? [group.group_name] : [],
          );
        }),
      ),
    ],
    [activeTrailingCanvases],
  );
  const trailingItemsRemain = useMemo(
    () =>
      activeTrailingCanvases.some(
        canvas => progressByPage[canvas.canvas_key ?? ''] !== 'completed',
      ),
    [activeTrailingCanvases, progressByPage],
  );

  const completionReady = useMemo(() => {
    const reviewPageKeys = activeCompletionPageKeys.filter(pageKey =>
      ['M6', 'P6'].includes(pageKey),
    );
    if (reviewPageKeys.length === 0) return false;
    // K1 lives in the key_questions section, so it never appears in
    // activeCompletionPageKeys (clinic_setup/your_intake only).

    return activeCompletionPageKeys.every(isCompletionPageComplete);
  }, [activeCompletionPageKeys, isCompletionPageComplete]);
  const completionPageReachable = completionReady || adminVerificationNeedsCompletionPage;
  const firstIncompleteCompletionPageKey = useMemo(
    () =>
      ORDERED_KEYS.find(pageKey => {
        if (!activeCompletionPageKeys.includes(pageKey)) return false;
        return !isCompletionPageComplete(pageKey);
      }) ?? 'K1',
    [activeCompletionPageKeys, isCompletionPageComplete],
  );

  useEffect(() => {
    if (isAggregateView) return;
    if (hydratedConfigurationScope !== configurationScope || !form) return;
    if (currentKey !== 'X1' || completionPageReachable) return;
    navigateToPage(firstIncompleteCompletionPageKey, true);
  }, [
    completionPageReachable,
    configurationScope,
    currentKey,
    firstIncompleteCompletionPageKey,
    form,
    hydratedConfigurationScope,
    isAggregateView,
    navigateToPage,
  ]);

  useEffect(() => {
    // A bookmarked or hand-typed /intake/items-we-need-later survives the
    // existence check above: the canvas is there, it is simply not one this
    // surface can collect anything on (notNeededKeys), so it would render a
    // fields page with no controls. The rail hides it and goNext/goBack skip
    // it; a direct route has to reach the same destination they would.
    if (isAggregateView) return;
    // Wait for the configuration to hydrate, exactly as the X1 guard does:
    // completionPageReachable reads false until it lands, and redirecting on
    // that would strand the provider on the first unfinished page.
    if (hydratedConfigurationScope !== configurationScope || !form) return;
    if (!TRAILING_PAGE_KEYS.includes(currentKey) || !notNeededKeys.has(currentKey)) return;
    navigateToPage(completionPageReachable ? 'X1' : firstIncompleteCompletionPageKey, true);
  }, [
    completionPageReachable,
    configurationScope,
    currentKey,
    firstIncompleteCompletionPageKey,
    form,
    hydratedConfigurationScope,
    isAggregateView,
    navigateToPage,
    notNeededKeys,
  ]);

  const reviewCanvases = useMemo(() => {
    const sectionKey =
      currentKey === 'M6' ? 'clinic_setup' : currentKey === 'P6' ? 'your_intake' : null;
    if (!sectionKey) return [];
    const sectionItems = RAIL_SECTIONS.find(entry => entry.key === sectionKey)?.items ?? [];
    const pageOrder = new Map(sectionItems.map((item, index) => [item.pageKey, index]));
    return (form?.canvases ?? [])
      .filter(
        canvas =>
          canvas.is_active &&
          canvas.section === sectionKey &&
          canvas.canvas_type !== 'summary' &&
          Boolean(canvas.canvas_key),
      )
      .sort(
        (left, right) =>
          (pageOrder.get(left.canvas_key ?? '') ?? Number.MAX_SAFE_INTEGER) -
          (pageOrder.get(right.canvas_key ?? '') ?? Number.MAX_SAFE_INTEGER),
      );
  }, [currentKey, form?.canvases]);
  const reviewItems = useMemo(() => {
    const sectionKey =
      currentKey === 'M6' ? 'clinic_setup' : currentKey === 'P6' ? 'your_intake' : null;
    if (!sectionKey) return [];
    const reviewPageKey = sectionKey === 'clinic_setup' ? 'M6' : 'P6';
    const section = RAIL_SECTIONS.find(entry => entry.key === sectionKey);
    return (section?.items ?? [])
      .filter(item => item.pageKey !== reviewPageKey && !notNeededKeys.has(item.pageKey))
      .filter(item => reviewCanvases.some(canvas => canvas.canvas_key === item.pageKey))
      .map(item => {
        const state: RailProgressState = completionByPage[item.pageKey]
          ? 'completed'
          : (progressByPage[item.pageKey] ?? 'notStarted');
        return { pageKey: item.pageKey, label: item.label, state };
      });
  }, [completionByPage, currentKey, notNeededKeys, progressByPage, reviewCanvases]);

  const foundDocuments: FoundDocument[] = useMemo(() => {
    const persisted = (documentInventory?.documents ?? []).map(documentItem =>
      persistedDocumentToFoundDocument(documentItem, allFields, decomposedReferenceIds),
    );
    const persistedArtifactIds = new Set(
      (documentInventory?.documents ?? []).flatMap(documentItem => {
        const artifactId = documentItem.document_reference.source_artifact_id;
        return artifactId ? [artifactId] : [];
      }),
    );
    const optimistic = pendingUploads
      .filter(pending => !persistedArtifactIds.has(pending.artifactId))
      .map(pending => {
        const info = docKinds[pending.artifactId];
        return {
          artifactId: pending.artifactId,
          filename: pending.filename,
          status: documentDisplayStatus(pending.status),
          docKind: info?.docKind ?? null,
          filledCount: info?.filled ?? 0,
          understood: pending.status !== 'failed',
        };
      });
    return [...optimistic, ...persisted];
  }, [pendingUploads, docKinds, documentInventory?.documents, allFields, decomposedReferenceIds]);
  const isDocumentProcessing =
    isUploadInFlight || foundDocuments.some(document => document.status === 'parsing');
  // Re-evaluated on every fetch (not just on data changes) so the age cutoff
  // in `documentInventoryPollingInterval` can end polling for a document that
  // never leaves `pending`.
  useEffect(() => {
    setDocumentPollingInterval(
      isUploadInFlight
        ? DOCUMENT_PROCESSING_POLL_INTERVAL_MS
        : documentInventoryPollingInterval(documentInventory?.documents, pendingUploads),
    );
  }, [documentInventory?.documents, documentInventoryFetchedAt, isUploadInFlight, pendingUploads]);

  // ---- navigation --------------------------------------------------------
  const goNext = useCallback(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let next = index + 1; next < ORDERED_KEYS.length; next += 1) {
      const key = ORDERED_KEYS[next];
      if (!notNeededKeys.has(key)) {
        navigateToPage(key);
        return;
      }
    }
    // The trailing page is the last rail slot, so "Finish" there means going
    // back to the completion page — or, for a provider who opened it from the
    // rail before finishing, to the page that still needs them. Never inert.
    if (TRAILING_PAGE_KEYS.includes(currentKey)) {
      navigateToPage(completionPageReachable ? 'X1' : firstIncompleteCompletionPageKey);
    }
  }, [
    completionPageReachable,
    currentKey,
    firstIncompleteCompletionPageKey,
    navigateToPage,
    notNeededKeys,
  ]);

  const goBack = useCallback(() => {
    // Back from the trailing page lands on X1, which bounces to the first
    // unfinished page when completion is not reachable. Resolve that here so
    // the provider never sees the completion page flash on the way.
    if (TRAILING_PAGE_KEYS.includes(currentKey)) {
      navigateToPage(completionPageReachable ? 'X1' : firstIncompleteCompletionPageKey);
      return;
    }
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let previous = index - 1; previous >= 0; previous -= 1) {
      const key = ORDERED_KEYS[previous];
      if (!notNeededKeys.has(key)) {
        navigateToPage(key);
        return;
      }
    }
  }, [
    completionPageReachable,
    currentKey,
    firstIncompleteCompletionPageKey,
    navigateToPage,
    notNeededKeys,
  ]);

  const handleCompletedPageContinue = useCallback(
    async (pageKey: IntakeConfig['completedPages'][number]) => {
      if (acknowledgedConfig.completedPages.includes(pageKey)) {
        goNext();
        return;
      }
      const nextConfig = {
        ...config,
        completedPages: [...new Set([...config.completedPages, pageKey])],
      };
      setConfig(nextConfig);

      // Admin browsing without a specific provider selected has nothing to
      // persist the completion against. Reflect it locally and advance
      // instead of silently stranding the user on the current page.
      if (isAdmin && (context.kind !== 'admin' || !context.userId)) {
        setAcknowledgedConfig(nextConfig);
        goNext();
        return;
      }
      setIsSavingPageCompletion(true);
      setAdminSaveErrorCount(0);
      setAdminSaveErrorMessage(undefined);
      setCompletionErrorPageKey(null);
      try {
        if (context.kind === 'admin') {
          await updateAdminConfiguration({
            organizationId: context.organizationId,
            userId: context.userId ?? '',
            intakeFormConfigurationUpdateRequest: intakeConfigurationUpdate(nextConfig),
          }).unwrap();
          // Same putV2AdminOrganizationsByOrganizationIdIntakeHubConfig
          // endpoint handleSaveAdminChanges refetches after — this call site
          // was missed. Await so goNext() doesn't advance before the headline
          // is fresh.
          await refetchDashboard();
        } else {
          await saveProviderConfigurationImmediately(nextConfig);
        }
        setAcknowledgedConfig(nextConfig);
        goNext();
      } catch {
        setConfig(previous => ({
          ...previous,
          completedPages: previous.completedPages.filter(key => key !== pageKey),
        }));
        setCompletionErrorPageKey(pageKey);
      } finally {
        setIsSavingPageCompletion(false);
      }
    },
    [
      acknowledgedConfig,
      config,
      context,
      goNext,
      isAdmin,
      refetchDashboard,
      saveProviderConfigurationImmediately,
      updateAdminConfiguration,
    ],
  );

  const nextLabel = useMemo(() => {
    const index = ORDERED_KEYS.indexOf(currentKey);
    for (let next = index + 1; next < ORDERED_KEYS.length; next += 1) {
      const key = ORDERED_KEYS[next];
      if (notNeededKeys.has(key)) continue;
      const label = RAIL_SECTIONS.flatMap(section => section.items).find(
        item => item.pageKey === key,
      )?.label;
      return `Next: ${label} →`;
    }
    return 'Finish →';
  }, [currentKey, notNeededKeys]);

  const currentLabel =
    RAIL_SECTIONS.flatMap(section => section.items).find(item => item.pageKey === currentKey)
      ?.label ?? currentKey;

  const currentPage = useMemo(
    () => (form?.canvases ?? []).find(canvas => canvas.canvas_key === currentKey),
    [form, currentKey],
  );
  const currentFields = useMemo(() => currentPage?.fields ?? [], [currentPage?.fields]);
  const currentGroups = currentPage?.groups ?? [];
  const draftValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.draftValue]),
      ),
    [fieldStates],
  );
  const acknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.acknowledgedValue]),
      ),
    [fieldStates],
  );
  const hasAcknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [
          fieldId,
          state.hasAcknowledgedValue,
        ]),
      ),
    [fieldStates],
  );
  const disabledReasons = useMemo(() => {
    const reasons: Record<string, string> = {};
    for (const field of allFields) {
      // IntakePageFieldInput owns catalog-declared read-only fields. Everything
      // writable is fail-closed here unless this component has a persistence
      // branch for the exact destination below.
      if (field.is_writable === false) continue;
      if (isAggregateView) {
        reasons[field.field_id] = 'Select a provider to edit this information.';
        continue;
      }

      if (field.destination.kind === 'intake_item_evidence') {
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!field.requirement_key || !item?.intake_item_id) {
          reasons[field.field_id] = 'This field is still being prepared. Refresh and try again.';
        }
        continue;
      }

      if (field.destination.kind === 'intake_item_sensitive') {
        if (isAdmin) {
          const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
          reasons[field.field_id] = item?.sensitive_fields_on_file?.includes(
            field.destination.field_key,
          )
            ? 'Secure value is on file; staff cannot replace it.'
            : 'The provider must complete this field through the secure flow.';
          continue;
        }
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!field.requirement_key || !item?.intake_item_id) {
          reasons[field.field_id] = 'This field is still being prepared. Refresh and try again.';
        }
        continue;
      }

      // intake_config, document_reference, and future destination kinds do
      // not have a generic page write path in handlePersistField.
      reasons[field.field_id] = 'Complete this information in its dedicated Intake Hub section.';
    }
    return reasons;
  }, [allFields, dashboard?.items, isAdmin, isAggregateView, userId]);

  const maskedSensitiveValues = useMemo(
    () => maskedSensitiveValuesByFieldId(allFields, dashboard?.items ?? [], userId),
    [allFields, dashboard?.items, userId],
  );

  const navigateFromRail = useCallback(
    (pageKey: string) => {
      if (pageKey === 'X1' && !completionPageReachable && !isAggregateView) {
        navigateToPage(firstIncompleteCompletionPageKey);
        return;
      }
      navigateToPage(pageKey);
    },
    [completionPageReachable, firstIncompleteCompletionPageKey, isAggregateView, navigateToPage],
  );

  const handleGoToDashboard = useCallback(() => {
    intakeHub.completionDashboardClicked({
      active_page_count: activeCompletionPageKeys.length,
      completed_page_count: activeCompletionPageKeys.filter(isCompletionPageComplete).length,
      percent_complete: percentComplete,
    });
    navigate('/dashboard');
  }, [activeCompletionPageKeys, intakeHub, isCompletionPageComplete, navigate, percentComplete]);

  const handleGoToTrailingItems = useCallback(() => {
    if (!trailingPageKey) return;
    // Admin surfaces do not emit product analytics (see CLAUDE.md), matching
    // handlePersistField's guard. The navigation itself stays available.
    if (!isAdmin) {
      intakeHub.trailingItemsClicked({
        items_remain: trailingItemsRemain,
        page_key: trailingPageKey,
      });
    }
    navigateToPage(trailingPageKey);
  }, [intakeHub, isAdmin, navigateToPage, trailingItemsRemain, trailingPageKey]);

  const handleDraftChange = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      setFieldStates(previous =>
        updateFieldStatesForDraftChange(allFields, previous, field, value),
      );
    },
    [allFields],
  );

  const handlePersistField = useCallback(
    async (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      if (!isAdmin) {
        intakeHub.fieldEditAttempted({
          destination_kind: field.destination.kind,
          field_id: field.field_id,
          field_type: field.data_type,
        });
      }

      if (field.destination.kind === 'intake_item_evidence') {
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!item?.intake_item_id || !form?.catalog_version) {
          throw new Error('Intake field destination is unavailable');
        }
        const intakeItemId = item.intake_item_id;
        let persistedResponse;
        if (context.kind === 'admin') {
          const adminUserId = context.userId;
          if (!adminUserId) {
            throw new Error('Intake evidence changes require an explicitly selected user');
          }
          persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
            updateAdminEvidence({
              organizationId: context.organizationId,
              userId: adminUserId,
              intakeItemId,
              updateItemEvidenceRequest: {
                catalog_version: form.catalog_version,
                extracted_fields: { [field.destination.field_key]: value },
              },
            }).unwrap(),
          );
        } else {
          persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
            updateProviderEvidence({
              intakeItemId,
              updateItemEvidenceProviderRequest: {
                catalog_version: form.catalog_version,
                extracted_fields: { [field.destination.field_key]: value },
              },
            }).unwrap(),
          );
        }
        storeAcceptedItem(persistedResponse.item);
        setFieldStates(previous => acknowledgePersistedFieldValue(previous, field.field_id, value));
        // The headline/analytics percentage is sourced from the backend's
        // profile_summary (item-based), not from local item state. Await the
        // refetch (rather than fire-and-forget) so callers that await this
        // save — the admin batch save and the per-field provider save — only
        // resolve once profile_summary is fresh; otherwise a fast "go to
        // dashboard" click can still race the refetch and read the pre-save
        // percentage.
        await refetchDashboard();
        return;
      }

      if (field.destination.kind === 'intake_item_sensitive') {
        if (isAdmin) throw new Error('Secure fields are provider-only');
        const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
        if (!item?.intake_item_id || typeof value !== 'string') {
          throw new Error('Secure field destination is unavailable');
        }
        const intakeItemId = item.intake_item_id;
        const fieldKey = field.destination.field_key;
        await writeQueue.enqueue(intakeItemId, () =>
          updateSensitiveField({
            intakeItemId,
            updateSensitiveFieldRequest: {
              field_key: fieldKey,
              value,
            },
          }).unwrap(),
        );
        setPendingSensitiveConfirmations(previous => new Set(previous).add(field.field_id));
        setFieldStates(previous =>
          acknowledgePersistedFieldValue(previous, field.field_id, value, true),
        );
        // Same reasoning as the intake_item_evidence branch above: await so a
        // fast "go to dashboard" click can't race the refetch and read the
        // pre-save profile_summary.
        await refetchDashboard();
        return;
      }

      throw new Error('Intake field destination is not writable here');
    },
    [
      form?.catalog_version,
      context,
      dashboard?.items,
      isAdmin,
      refetchDashboard,
      storeAcceptedItem,
      intakeHub,
      updateAdminEvidence,
      updateProviderEvidence,
      updateSensitiveField,
      writeQueue,
      userId,
    ],
  );

  const handlePersistRepeatGroup = useCallback(
    async (fields: GeneratedIntakeField[], records: IntakeRepeatRecord[]) => {
      const field = fields[0];
      if (!field || field.destination.kind !== 'intake_item_evidence') {
        throw new Error('Repeated intake field destination is unavailable');
      }
      const item = findIntakeItemForField(field, dashboard?.items ?? [], userId);
      if (!item?.intake_item_id || !form?.catalog_version) {
        throw new Error('Repeated intake field destination is unavailable');
      }
      const groupKey = repeatGroupKey(field);
      const serverRecords = serverRepeatRecordsByGroup[groupKey] ?? records;
      setRepeatGroupStates(previous => {
        const existing = previous[groupKey] ?? {
          acknowledgedRecords: serverRecords,
          draftRecords: serverRecords,
        };
        return {
          ...previous,
          [groupKey]: { ...existing, draftRecords: records },
        };
      });
      const intakeItemId = item.intake_item_id;
      const extractedFields = repeatRecordsEvidence(records);
      let persistedResponse;
      if (context.kind === 'admin') {
        if (!context.userId) {
          throw new Error('Intake evidence changes require an explicitly selected user');
        }
        persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
          updateAdminEvidence({
            organizationId: context.organizationId,
            userId: context.userId,
            intakeItemId,
            updateItemEvidenceRequest: {
              catalog_version: form.catalog_version,
              extracted_fields: extractedFields,
            },
          }).unwrap(),
        );
      } else {
        persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
          updateProviderEvidence({
            intakeItemId,
            updateItemEvidenceProviderRequest: {
              catalog_version: form.catalog_version,
              extracted_fields: extractedFields,
            },
          }).unwrap(),
        );
      }
      storeAcceptedItem(persistedResponse.item);
      setRepeatGroupStates(previous => {
        const existing = previous[groupKey] ?? {
          acknowledgedRecords: serverRecords,
          draftRecords: records,
        };
        return {
          ...previous,
          [groupKey]: {
            acknowledgedRecords: records,
            draftRecords: repeatRecordsEqual(existing.draftRecords, records)
              ? records
              : existing.draftRecords,
          },
        };
      });
      // Await for the same reason as the single-field evidence save above:
      // fire-and-forget can let a fast "go to dashboard" click race the
      // refetch and read the pre-save profile_summary.
      await refetchDashboard();
    },
    [
      context,
      dashboard?.items,
      form?.catalog_version,
      refetchDashboard,
      serverRepeatRecordsByGroup,
      storeAcceptedItem,
      updateAdminEvidence,
      updateProviderEvidence,
      userId,
      writeQueue,
    ],
  );

  const handlePersistPayerRoster = useCallback(
    async (rows: IntakePayerRosterEntry[]) => {
      if (context.kind !== 'admin') {
        throw new Error('Payer roster changes require staff access');
      }
      const catalogVersion =
        form?.catalog_version ?? payerRosterItem?.evidence_summary?.catalog_version;
      if (!payerRosterItem?.intake_item_id) {
        throw new Error('Payer roster destination is unavailable');
      }
      if (!catalogVersion) {
        throw new Error('Payer roster catalog version is unavailable');
      }
      const intakeItemId = payerRosterItem.intake_item_id;
      const persistedResponse = await writeQueue.enqueue(intakeItemId, () =>
        updateAdminEvidence({
          organizationId: context.organizationId,
          intakeItemId,
          updateItemEvidenceRequest: {
            catalog_version: catalogVersion,
            payer_roster: rows,
          },
        }).unwrap(),
      );
      storeAcceptedItem(persistedResponse.item);
      setAcknowledgedPayerRoster(rows);
      // Await for the same reason as the single-field evidence save above:
      // fire-and-forget can let a fast "go to dashboard" click race the
      // refetch and read the pre-save profile_summary.
      await refetchDashboard();
    },
    [
      context,
      form?.catalog_version,
      payerRosterItem?.evidence_summary?.catalog_version,
      payerRosterItem?.intake_item_id,
      refetchDashboard,
      storeAcceptedItem,
      updateAdminEvidence,
      writeQueue,
    ],
  );

  const handleConfigurationChange = useCallback(
    (patch: Partial<IntakeConfig>) => {
      if (isAggregateView) return;
      setConfig(previous => {
        const next = { ...previous, ...patch };
        if (!isAdmin) triggerProviderConfigurationSave(next);
        return next;
      });
    },
    [isAdmin, isAggregateView, triggerProviderConfigurationSave],
  );

  const handleAddPayer = useCallback((payer: PayerRow) => {
    setPayerRosterDraft(previous => [
      ...previous,
      {
        enrollment_status: payer.enrollmentStatus,
        jotbill: payer.jotbill,
        jotcred: payer.jotcred,
        payer_id: payer.payerId,
        payer_name: payer.displayName,
      },
    ]);
  }, []);

  const handleRemovePayer = useCallback((payerId: string) => {
    setPayerRosterDraft(previous => previous.filter(payer => payer.payer_id !== payerId));
  }, []);

  const handlePayerEnrollmentStatusChange = useCallback(
    (payerId: string, enrollmentStatus: PayerRow['enrollmentStatus']) => {
      setPayerRosterDraft(previous =>
        previous.map(payer =>
          payer.payer_id === payerId
            ? {
                ...payer,
                enrollment_status: enrollmentStatus,
                jotcred: enrollmentStatus === 'wants_to_enroll',
              }
            : payer,
        ),
      );
    },
    [],
  );

  const handlePayerServiceChange = useCallback(
    (payerId: string, service: 'jotcred' | 'jotbill', enabled: boolean) => {
      setPayerRosterDraft(previous =>
        previous.map(payer =>
          payer.payer_id === payerId ? { ...payer, [service]: enabled } : payer,
        ),
      );
    },
    [],
  );

  const handleSaveAdminChanges = useCallback(
    async (fieldsToSave: GeneratedIntakeField[]) => {
      const dirtyFields = fieldsToSave.filter(field => {
        const state = fieldStates[field.field_id];
        return (
          state &&
          JSON.stringify(state.draftValue) !== JSON.stringify(state.acknowledgedValue) &&
          !disabledReasons[field.field_id]
        );
      });
      const configurationIsDirty = !configurationsMatch(config, acknowledgedConfig);
      if (dirtyFields.length === 0 && !configurationIsDirty && !payerRosterDirty) return;

      setIsSavingAdminChanges(true);
      setAdminSaveErrorCount(0);
      setAdminSaveErrorMessage(undefined);
      const saves: Array<{ kind: AdminSaveKind; promise: Promise<unknown> }> = dirtyFields.map(
        field => ({
          kind: 'field',
          promise: handlePersistField(field, fieldStates[field.field_id].draftValue),
        }),
      );
      if (payerRosterDirty) {
        saves.push({ kind: 'payer', promise: handlePersistPayerRoster(payerRosterDraft) });
      }
      if (configurationIsDirty && context.kind === 'admin' && context.userId) {
        saves.push({
          kind: 'configuration',
          promise: updateAdminConfiguration({
            organizationId: context.organizationId,
            userId: context.userId,
            intakeFormConfigurationUpdateRequest: intakeConfigurationUpdate(config),
          })
            .unwrap()
            .then(() => setAcknowledgedConfig(config))
            // putV2AdminOrganizationsByOrganizationIdIntakeHubConfig also
            // invalidates the IntakeHubDashboard tag (store/api.ts), so a
            // config save can change profile_summary too — refetch so this
            // save doesn't clear its spinner before the headline is fresh.
            .then(() => refetchDashboard()),
        });
      }
      const results = await Promise.allSettled(saves.map(save => save.promise));
      const failedKinds = results.flatMap((result, index) =>
        result.status === 'rejected' ? [saves[index].kind] : [],
      );
      setAdminSaveErrorCount(failedKinds.length);
      setAdminSaveErrorMessage(formatAdminSaveErrorMessage(failedKinds));
      setIsSavingAdminChanges(false);
    },
    [
      acknowledgedConfig,
      config,
      context,
      disabledReasons,
      fieldStates,
      handlePersistField,
      handlePersistPayerRoster,
      payerRosterDirty,
      payerRosterDraft,
      refetchDashboard,
      updateAdminConfiguration,
    ],
  );

  const resolveAcceptedSuggestionRequests = useCallback(
    (suggestions: IntakeFieldSuggestion[]): IntakeFieldSuggestionAcceptanceRequest[] =>
      suggestions.map(suggestion => {
        const documentReferenceId =
          documentSuggestionReferenceIds[
            suggestionEntryKey(suggestion.field_id, suggestion.repeat_record_id)
          ];
        if (!documentReferenceId) throw new Error('Document suggestion is unavailable');
        return {
          intake_item_id: suggestion.target_intake_item_id,
          document_reference_id: documentReferenceId,
          extraction_id: suggestion.extraction_id,
          field_id: suggestion.field_id,
          /* The contract's compare-and-swap pairing: the suggestion response
             now carries the field's current_value, and acceptance echoes it
             back so the server can reject if the field changed since the
             suggestion was computed. */
          expected_current_value: suggestion.current_value,
          ...(suggestion.repeat_record_id ? { repeat_record_id: suggestion.repeat_record_id } : {}),
        };
      }),
    [documentSuggestionReferenceIds],
  );

  const enqueueSuggestionAcceptance = useCallback(
    (operationId: string, write: () => Promise<void>): Promise<void> => {
      const existing = acceptanceOperations.current.get(operationId);
      if (existing) return existing;

      const operation = acceptanceOperationTail.current.then(write);
      acceptanceOperations.current.set(operationId, operation);
      const normalizedTail = operation.then(
        () => undefined,
        () => undefined,
      );
      acceptanceOperationTail.current = normalizedTail;
      void normalizedTail.then(() => {
        if (acceptanceOperationTail.current === normalizedTail) {
          acceptedSuggestionRequestIds.current.clear();
        }
      });
      void operation
        .finally(() => {
          if (acceptanceOperations.current.get(operationId) === operation) {
            acceptanceOperations.current.delete(operationId);
          }
        })
        .catch(() => undefined);
      return operation;
    },
    [],
  );

  const persistAcceptedSuggestions = useCallback(
    async (
      suggestions: IntakeFieldSuggestion[],
      requests: IntakeFieldSuggestionAcceptanceRequest[],
    ) => {
      if (isAdmin) throw new Error('Document suggestions are provider-only');
      const response = await writeQueue.enqueueMany(
        suggestions.map(suggestion => suggestion.target_intake_item_id),
        () =>
          acceptDocumentFieldSuggestions({
            acceptDocumentFieldSuggestionsRequest: { suggestions: requests },
          }).unwrap(),
      );
      return { requests, response };
    },
    [acceptDocumentFieldSuggestions, isAdmin, writeQueue],
  );

  const handleAcceptSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => {
      let requests: IntakeFieldSuggestionAcceptanceRequest[];
      try {
        requests = resolveAcceptedSuggestionRequests([suggestion]);
      } catch {
        void refetchDashboard();
        void refetchDocumentInventory();
        return Promise.reject(new SuggestionAcceptanceError('uncertain'));
      }
      const requestId = acceptanceRequestIdentity(requests[0]);
      const operationId = acceptanceOperationIdentity(requests);

      return enqueueSuggestionAcceptance(operationId, async () => {
        if (acceptedSuggestionRequestIds.current.has(requestId)) return;
        intakeHub.suggestionActionClicked({ action: 'accept', suggestion_count: 1 });
        let persisted;
        try {
          persisted = await persistAcceptedSuggestions([suggestion], requests);
        } catch {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        if (!hasExactlyCorrelatedAcceptanceResults(persisted.requests, persisted.response)) {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        if (persisted.response.results[0].outcome !== 'accepted') {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('stale');
        }
        const acceptedItem = persisted.response.results[0].item;
        if (!acceptedItem) {
          void refetchDashboard();
          void refetchDocumentInventory();
          throw new SuggestionAcceptanceError('uncertain');
        }
        storeAcceptedItem(acceptedItem);
        acceptedSuggestionRequestIds.current.add(requestId);
        setFieldStates(previous =>
          replaceFieldValueWithAcceptedSuggestion(previous, suggestion.field_id, suggestion.value),
        );
        // Accepting a repeated suggestion can create a record server-side, which
        // re-targets the sibling suggestions still on screen. Their
        // `repeat_record_id` / `current_value` pair must come from a read taken
        // after that, so refresh the inventory rather than accepting a stale
        // pair on the next click.
        if (suggestion.repeat_record_id) void refetchDocumentInventory();
        // The headline/analytics percentage reads dashboard.profile_summary,
        // which storeAcceptedItem's local item-authority overlay does not
        // touch — without this, accepting a suggestion would leave the
        // headline showing the pre-acceptance percentage.
        await refetchDashboard();
      });
    },
    [
      enqueueSuggestionAcceptance,
      intakeHub,
      persistAcceptedSuggestions,
      refetchDashboard,
      refetchDocumentInventory,
      resolveAcceptedSuggestionRequests,
      storeAcceptedItem,
    ],
  );

  const handleDiscardSuggestion = useCallback(
    (suggestion: IntakeFieldSuggestion) => {
      intakeHub.suggestionActionClicked({ action: 'discard', suggestion_count: 1 });
      setDiscardedSuggestionIds(previous => {
        const next = new Set(previous);
        next.add(suggestionIdentity(suggestion));
        return next;
      });
    },
    [intakeHub],
  );

  const handleHelpAction = useCallback((action: 'message_specialist') => {
    // Help-panel specialist requests go to billing support by email rather
    // than the in-app chat widget.
    if (action === 'message_specialist')
      window.location.href = 'mailto:billingsupport@jotpsych.com';
  }, []);

  if (materializationStatus === 'failed' && hasMissingMaterializedDestinations) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void runMaterialization()}>
              Retry item preparation
            </Button>
          }
        >
          We couldn&apos;t prepare your intake items.
        </Alert>
      </Box>
    );
  }

  if (dashboardError && !dashboard) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={isDashboardFetching}
              onClick={() => void refetchDashboard()}
            >
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your intake. Please try again.
        </Alert>
      </Box>
    );
  }

  if (configurationError && hydratedConfigurationScope !== configurationScope) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, py: 6 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void refetchConfiguration()}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load this intake configuration. No changes have been made.
        </Alert>
      </Box>
    );
  }

  if (
    isPreparingMissingItems ||
    (isConfigurationFetching && hydratedConfigurationScope !== configurationScope) ||
    hydratedConfigurationScope !== configurationScope ||
    (!serverConfiguration && !isAggregateView)
  ) {
    return (
      <LinearProgress
        aria-label={
          isPreparingMissingItems ? 'Preparing intake items' : 'Loading intake configuration'
        }
      />
    );
  }

  return (
    <IntakeShell
      organizationName={dashboard?.clinician?.organization_id ? 'Your practice' : 'Your practice'}
      currentPageKey={currentKey}
      notNeededKeys={notNeededKeys}
      gateOpen={gateOpen}
      percentComplete={percentComplete}
      onNavigate={navigateFromRail}
      pendingByPage={suggestionsByPage}
      progressByPage={progressByPage}
      lockedPageKeys={completionPageReachable || isAggregateView ? undefined : new Set(['X1'])}
    >
      {currentKey === 'K1' && (
        <>
          {!isAdmin && !isAggregateView && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <AutosaveStatus
                status={providerConfigurationSaveStatus}
                isDirty={hasPendingProviderConfigurationSave()}
                lastSavedAt={providerConfigurationLastSavedAt}
                verbose
              />
            </Box>
          )}
          <IntakeConfigPage
            config={effectiveConfig}
            onChange={handleConfigurationChange}
            payerRows={payerRows}
            payerTableProps={{
              canManage: isAdmin,
              loading: isDashboardFetching && !dashboard,
              onEnrollmentStatusChange: isAdmin ? handlePayerEnrollmentStatusChange : undefined,
              onRemove: isAdmin ? handleRemovePayer : undefined,
              onServiceChange: isAdmin ? handlePayerServiceChange : undefined,
              search: isAdmin ? (
                <IntakeAdminPayerSearch existingRows={payerRows} onAdd={handleAddPayer} />
              ) : undefined,
            }}
            eligibleProviders={eligibleProviders}
            eligibleProvidersLoading={eligibleProvidersLoading}
            eligibleProvidersError={Boolean(eligibleProvidersQueryError)}
            rosterCandidates={rosterCandidates}
            canManageRoster={canManageRoster}
            onAddRosterProvider={handleAddRosterProvider}
            onRemoveRosterProvider={handleRemoveRosterProvider}
            isAddingRosterProvider={isAddingRosterProvider}
            derivedSentence={derivedSentence}
            groups={currentGroups}
            onHelpAction={handleHelpAction}
            organizationScope={isAggregateView}
            readOnlyReason={
              isAggregateView
                ? 'These settings belong to the organization. Provider sections remain grouped by provider.'
                : undefined
            }
            continueLabel={isAggregateView ? null : "See what we'll need →"}
            onContinue={() => {
              setGateOpenedInSession(true);
              navigateToPage('F1');
            }}
            saveLabel={isAdmin ? (isSavingAdminChanges ? 'Saving…' : 'Save changes') : undefined}
            onSave={isAdmin ? () => void handleSaveAdminChanges(currentFields) : undefined}
            saveDisabled={
              isAdmin &&
              (isSavingAdminChanges ||
                (configurationsMatch(config, acknowledgedConfig) && !payerRosterDirty))
            }
            saveErrorMessage={isAdmin ? adminSaveErrorMessage : undefined}
            saveMode={isAdmin ? 'explicit' : 'autosave'}
            isSaving={isSavingAdminChanges}
            saveErrorCount={adminSaveErrorCount}
          />
        </>
      )}

      {isAggregateView && currentKey !== 'K1' && context.kind === 'admin' && (
        <AggregateIntakePage
          aggregate={aggregateView}
          currentPageKey={currentKey}
          error={aggregateViewError}
          isFetching={isAggregateViewFetching}
          organizationId={context.organizationId}
          onPageChange={setAggregatePage}
          onRetry={() => void refetchAggregateView()}
        />
      )}

      {!isAggregateView && currentKey === 'F1' && (
        <IntakeForecastPage
          pages={pageStats}
          documents={documentsToGather}
          totalRequired={totalRequired}
          onContinue={() => {
            // A save is already in flight (isSavingPageCompletion), so a rapid repeat click
            // — before the button below has re-rendered as disabled — must not re-track the
            // event or start a second configuration update. handleCompletedPageContinue's own
            // "already acknowledged" short-circuit only helps *after* the first save resolves;
            // this guards the window while it's still pending.
            if (isSavingPageCompletion || isProviderConfigurationHydrating) return;
            // Only the first acknowledgement is a real conversion event — a revisit to an
            // already-completed F1 short-circuits inside handleCompletedPageContinue and
            // just navigates on, so tracking it again would inflate the metric.
            if (!isAdmin && !acknowledgedConfig.completedPages.includes('F1')) {
              intakeHub.forecastAcknowledgedClicked({ total_required: totalRequired });
            }
            void handleCompletedPageContinue('F1');
          }}
          onBack={goBack}
          nextLabel={nextLabel}
          errorMessage={
            completionErrorPageKey === 'F1'
              ? 'We could not save your acknowledgement. Try again.'
              : undefined
          }
          disabled={isSavingPageCompletion || isProviderConfigurationHydrating}
          disabledReason={
            isSavingPageCompletion
              ? 'Wait for your acknowledgement to finish saving before continuing.'
              : isProviderConfigurationHydrating
                ? 'Loading your configuration before continuing.'
                : undefined
          }
        />
      )}

      {!isAggregateView && currentKey === 'M5' && (
        <IntakeProviderRosterPage
          providers={eligibleProviders}
          isLoading={eligibleProvidersLoading}
          isError={Boolean(eligibleProvidersQueryError)}
          onRetry={() => void refetchProviderCandidates()}
          onContinue={() => {
            if (providerRosterEntries.length === 0) {
              goNext();
              return;
            }
            void handleCompletedPageContinue('M5');
          }}
          onBack={goBack}
          nextLabel={nextLabel}
          errorMessage={
            completionErrorPageKey === 'M5'
              ? 'Your provider roster acknowledgement could not be saved. Try again.'
              : undefined
          }
        />
      )}

      {!isAggregateView && (currentKey === 'M6' || currentKey === 'P6') && (
        <IntakeReviewPage
          sectionLabel={currentKey === 'M6' ? 'Clinic setup' : 'Your intake'}
          items={reviewItems.map(item => {
            const canvas = reviewCanvases.find(entry => entry.canvas_key === item.pageKey);
            const fields = canvas?.fields ?? [];
            const groups = canvas?.groups ?? [];
            const canvasDocuments = foundDocuments.filter(document =>
              documentMatchesFields(document, fields, dashboard?.items ?? [], userId),
            );
            let content: ReactNode;

            if (item.pageKey in DOCUMENT_PAGES) {
              const documentPage = DOCUMENT_PAGES[item.pageKey];
              const requiredItems = new Map<
                string,
                { fields: GeneratedIntakeField[]; intakeItem?: GeneratedIntakeItem }
              >();
              requiredDocumentFields(fields).forEach(field => {
                const intakeItem = findIntakeItemForField(field, dashboard?.items ?? [], userId);
                const key =
                  intakeItem?.intake_item_id ??
                  `${field.view}:${field.requirement_key ?? field.field_id}`;
                const existing = requiredItems.get(key);
                requiredItems.set(key, {
                  fields: [...(existing?.fields ?? []), field],
                  intakeItem,
                });
              });
              const missingRequiredDocumentCount = [...requiredItems.values()].filter(
                requirement =>
                  !requirement.intakeItem?.is_complete &&
                  !canvasDocuments.some(document =>
                    documentMatchesFields(
                      document,
                      requirement.fields,
                      dashboard?.items ?? [],
                      userId,
                    ),
                  ),
              ).length;
              content = (
                <IntakeDocumentsPage
                  title={documentPage.title}
                  sub={documentPage.sub}
                  dropHint={documentPage.hint}
                  documents={canvasDocuments}
                  onDismissFailed={dismissByArtifactId}
                  getDocumentUrl={getDocumentUrl}
                  isDragging={isDragging}
                  onFiles={handleFiles}
                  landedSummary={landedSummary}
                  groups={groups}
                  onHelpAction={handleHelpAction}
                  onContinue={() => undefined}
                  nextLabel=""
                  embedded
                  missingRequiredDocumentCount={missingRequiredDocumentCount}
                  uploadingCount={uploadingFileCount}
                  showRowDownload={isAdmin}
                  requirements={documentRequirementStatuses(
                    fields,
                    dashboard?.items ?? [],
                    userId,
                    documentByReferenceId,
                  )}
                />
              );
            } else if (item.pageKey === 'M5') {
              content = (
                <IntakeProviderRosterPage
                  providers={eligibleProviders}
                  isLoading={eligibleProvidersLoading}
                  isError={Boolean(eligibleProvidersQueryError)}
                  onRetry={() => void refetchProviderCandidates()}
                  onContinue={() => undefined}
                  onBack={() => undefined}
                  nextLabel=""
                  embedded
                />
              );
            } else {
              content = (
                <IntakeFieldsPage
                  title={canvas?.canvas_name ?? item.label}
                  fields={fields}
                  groups={groups}
                  suggestions={documentSuggestions}
                  suggestionDocumentReferenceIds={documentSuggestionReferenceIds}
                  documentNameByReferenceId={documentNameByReferenceId}
                  onAcceptSuggestion={handleAcceptSuggestion}
                  onDiscardSuggestion={handleDiscardSuggestion}
                  onContinue={() => undefined}
                  nextLabel=""
                  draftValues={draftValues}
                  acknowledgedValues={acknowledgedValues}
                  hasAcknowledgedValues={hasAcknowledgedValues}
                  onDraftChange={handleDraftChange}
                  onPersistField={handlePersistField}
                  repeatRecordsByGroup={repeatRecordsByGroup}
                  onPersistRepeatGroup={handlePersistRepeatGroup}
                  onHelpAction={handleHelpAction}
                  maskedSensitiveValues={maskedSensitiveValues}
                  disabledReasons={disabledReasons}
                  saveMode={isAdmin ? 'explicit' : 'autosave'}
                  isSaving={isSavingAdminChanges}
                  saveErrorCount={adminSaveErrorCount}
                  onSaveChanges={isAdmin ? () => void handleSaveAdminChanges(fields) : undefined}
                  embedded
                  showRequiredErrors
                />
              );
            }

            return { ...item, content };
          })}
          onContinue={() => {
            if (!isAdmin) {
              intakeHub.reviewConfirmedClicked({
                section: currentKey === 'M6' ? 'clinic_setup' : 'your_intake',
              });
            }
            void handleCompletedPageContinue(currentKey === 'M6' ? 'M6' : 'P6');
          }}
          onBack={goBack}
          errorMessage={
            completionErrorPageKey === (currentKey === 'M6' ? 'M6' : 'P6')
              ? 'Your review acknowledgement could not be saved. Try again.'
              : undefined
          }
          primaryLabel={
            context.kind === 'admin'
              ? context.userId
                ? `Mark reviewed on behalf of ${context.targetLabel ?? context.providerEmail ?? 'selected provider'}`
                : 'Mark reviewed'
              : 'Looks good'
          }
          disabledReason={
            isDocumentProcessing
              ? 'Wait for your documents to finish processing before continuing.'
              : isSavingAdminChanges || isSavingPageCompletion
                ? 'Wait for your changes to finish saving before continuing.'
                : adminSaveErrorCount > 0
                  ? 'Resolve the changes that could not be saved before continuing.'
                  : undefined
          }
        />
      )}

      {!isAggregateView && currentKey === 'X1' && completionPageReachable && (
        <IntakeCompletionPage
          onGoToDashboard={isAdmin ? undefined : handleGoToDashboard}
          trailingItemLabels={trailingItemLabels}
          trailingItemsRemain={trailingItemsRemain}
          onGoToTrailingItems={trailingPageKey ? handleGoToTrailingItems : undefined}
          canVerifyIntake={canVerifyIntake && Boolean(dashboard?.journey_status?.submitted_at)}
          verifiedAt={dashboard?.journey_status?.verified_at}
          onVerifyIntake={() => handleVerifyAdminIntake()}
          onDiscardVerification={() => handleDiscardAdminVerification()}
          isVerifyingIntake={isVerifyingAdminIntake}
          isDiscardingVerification={isDiscardingAdminVerification}
          verifyBlockedReason={
            hasUnsavedAdminChanges || isSavingAdminChanges || adminSaveErrorCount > 0
              ? 'Save all Admin changes before verifying.'
              : undefined
          }
        />
      )}

      {!isAggregateView && currentKey in DOCUMENT_PAGES && (
        <IntakeDocumentsPage
          title={DOCUMENT_PAGES[currentKey].title}
          sub={DOCUMENT_PAGES[currentKey].sub}
          dropHint={DOCUMENT_PAGES[currentKey].hint}
          documents={foundDocuments}
          onDismissFailed={dismissByArtifactId}
          getDocumentUrl={getDocumentUrl}
          isDragging={isDragging}
          onFiles={handleFiles}
          landedSummary={landedSummary}
          onReview={
            firstPageWithSuggestions ? () => navigateToPage(firstPageWithSuggestions) : undefined
          }
          reviewLabel={
            firstPageWithSuggestions ? `Review ${pageLabel(firstPageWithSuggestions)} →` : undefined
          }
          groups={currentGroups}
          readOnly={isAggregateView}
          onHelpAction={handleHelpAction}
          onContinue={goNext}
          onBack={goBack}
          nextLabel={nextLabel}
          uploadingCount={uploadingFileCount}
          showRowDownload={isAdmin}
          requirements={documentRequirementStatuses(
            currentFields,
            dashboard?.items ?? [],
            userId,
            documentByReferenceId,
          )}
          onDownloadAll={isAdmin ? handleDownloadAllDocuments : undefined}
          isDownloadingAll={isDownloadingAllDocuments}
        />
      )}

      {!isAggregateView &&
        !['K1', 'F1', 'M5', 'M6', 'P6', 'X1'].includes(currentKey) &&
        !(currentKey in DOCUMENT_PAGES) && (
          <>
            <IntakeFieldsPage
              title={currentLabel}
              fields={currentFields}
              groups={currentGroups}
              suggestions={documentSuggestions}
              suggestionDocumentReferenceIds={documentSuggestionReferenceIds}
              documentNameByReferenceId={documentNameByReferenceId}
              onAcceptSuggestion={handleAcceptSuggestion}
              onDiscardSuggestion={handleDiscardSuggestion}
              onContinue={goNext}
              onBack={goBack}
              nextLabel={nextLabel}
              draftValues={draftValues}
              acknowledgedValues={acknowledgedValues}
              hasAcknowledgedValues={hasAcknowledgedValues}
              onDraftChange={handleDraftChange}
              onPersistField={handlePersistField}
              repeatRecordsByGroup={repeatRecordsByGroup}
              onPersistRepeatGroup={handlePersistRepeatGroup}
              onHelpAction={handleHelpAction}
              maskedSensitiveValues={maskedSensitiveValues}
              disabledReasons={disabledReasons}
              saveMode={isAdmin ? 'explicit' : 'autosave'}
              isSaving={isSavingAdminChanges}
              saveErrorCount={adminSaveErrorCount}
              onSaveChanges={isAdmin ? () => void handleSaveAdminChanges(currentFields) : undefined}
            />
          </>
        )}

      {(isDragging || isUploadInFlight) && (
        <ArtifactDropOverlay
          subtitle={isUploadInFlight ? 'Keep this page open while the upload finishes.' : undefined}
          isUploading={isUploadInFlight}
          uploadingMessage={`Uploading ${uploadingFileCount} file${
            uploadingFileCount === 1 ? '' : 's'
          }`}
        />
      )}
    </IntakeShell>
  );
}
